/**
 * StrategiesSection.js - Support at Home "Optimise Your Plan" / strategies tab.
 * Manual and suggestion strategies, projections modal, aged care integration.
 */
import { anyoneOwnsHome } from '../../../../shared/src/calculation/householdPosition.ts';
import { sahHouseholdFingerprint } from './sahChosenStale.ts';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    pensionClause,
    cashflowClause,
    estateClause,
    plainClause,
    RecapClauses,
    type RecapClause,
} from '../../components/strategy/recapClauses.tsx';
import { Home, ShieldCheck, Info, TrendingUp, TrendingUp as TrendingUpIcon, TrendingDown, Users, Gift, Eye, Wrench, AlertCircle } from 'lucide-react';
import { getAgedCareModellerResidencyOverrides, isIllnessSeparatedCouple } from '../../../../shared/src/calculation/householdStatus.ts';
import { buildNetWorthSchedule } from '../../../../shared/src/calculation/netWorthSchedule.ts';
import * as calculationApi from '../../services/calculationApi.ts';
import { extractAgedCareIntegrationDetails, applyRadFundingToFactFind } from '../../strategy/StrategyIntegrationUtils.ts';
import { buildAnnuityImpact, summariseAnnuityImpact, applyAnnuityToFactFind } from '../../../../shared/src/calculation/annuityStrategies.ts';
import { getServicePlanFromStorage, getServicePlanWithSource, listSavedServicePlans, setCurrentServicePlan, clearCurrentServicePlan, usableSavedServicePlans, calculateServicePlanCosts, servicePlanBudgetsOf, type ServicePlanSource, type ServicePlanCosts } from '../../../ServicePlanUtils.ts';
import { calculateAge } from '../../utils/index.ts';
import { CurrencyInput, applyDownsizeToFactFind, applyRenovateToFactFind, applyUpgradeToFactFind } from './SupportAtHomeHelpers.tsx';
import {
    SupportDownsizeStrategyAllocator,
    ReverseMortgageStrategyAllocator,
    HEASStrategyAllocator,
    RenovateStrategyAllocator,
    UpgradeStrategyAllocator,
} from './SupportAtHomePropertyAllocators.tsx';
import { CalculationTooltip, GradientText, Button, Badge, ImpactMetric } from '../../components/ui/index.tsx';
import { useDoNothingProjection, type DoNothingRow } from '../../utils/doNothingBaseline.ts';
import { calculateSahOptimizationTargets, resolveIllnessSeparatedForSahInsight, type BreakdownRow } from './supportAtHomePensionInsightUtils.tsx';
import { sahPlanWords, sahLeverKinds, abpDrawChangesBetween, abpDrawSentences, type SahConfigLike } from './sahPlanWords.ts';
import { sahCardRecord, takeSahHubIntent } from './sahChosenPlan.ts';
import { sahMoveAnswer, sahPricedServicePlan, chosenAgedCarePlanOf, sahBaselineFactFind, sahLinkedCareFor, sahEnteringPerson, sahProjectionArgs } from './sahEngineContext.ts';
import { savedPlanTitle } from '../agedCare/planWords.ts';
import { useSahLinkedPosition } from './useSahLinkedPosition.ts';
import { useCalcTracker } from '../../utils/calcTracker.ts';
import SahCareUpDialog from './SahCareUpDialog.tsx';
import { scrollAppToTop } from '../../utils/scrollAppToTop.ts';
import { sahMovedAmount } from './SahPlansView.tsx';
import RailPlanActions from '../../components/strategy/RailPlanActions.tsx';
import { SahCauseEffect, SahFigurePopover, type SahFigures, type SahFigurePage } from './SahPlanDialogs.tsx';
import StrategyRisksPanel from '../../components/strategy/StrategyRisksPanel.tsx';
import { buildStrategyRisks, riskKindsFromSupportAtHomeType } from '../../strategy/strategyRisks.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { IncomeOnlyBeforeChoose, isIncomeOnlyAnnuity, incomeOnlyHeld, incomeOnlySetter } from '../../components/strategy/IncomeOnlyTick.tsx';
import { guaranteedShapeAvailable } from '../../../../shared/src/calculation/carePlusSplit.ts';
import AnnuityFamilyPanel from '../../components/strategy/AnnuityFamilyPanel.tsx';
import { sahRecordAnnuityOf, sahAnnuityPersonName, planAnnuityIncomeOf, SAH_COMPARE_NOTE } from './sahGuaranteedIncome.ts';
import { sahAnnuityInvestable } from '../../strategy/SupportAtHomeStrategyGenerator.ts';
import { ANNUAL_REDUCTION_PER_1000_ASSETS } from '../../constants/index.ts';
import ProjectionsModal from '../agedCare/ProjectionsModal.tsx';
import { readPeriodMode, type ProjectionsModalPeriodMode } from '../agedCare/projectionsModalPreferences.ts';
import {
    buildSupportAtHomeDigitalSOASnapshot,
    openSupportAtHomeDigitalSOAAttachment,
} from './SupportAtHomeDigitalSOAAttachment.tsx';
import DebtPayoffStrategy from '../agedCare/DebtPayoffStrategy.tsx';
import type { DebtPayoff } from '../../../../shared/src/calculation/support/debtPayoff.ts';
import SupportAtHomeAnnuityConfigurator, { DEFAULT_NEW_ANNUITY_RESIDUAL } from './SupportAtHomeAnnuityConfigurator.tsx';
import type { AnnuityDetails, AnnuityFundingSource } from './SupportAtHomeAnnuityConfigurator.tsx';
import SupportAtHomeSuggestionEnginePanel from './SupportAtHomeSuggestionEnginePanel.tsx';
import type { SahIntegrationSummary } from './SahPlanRecap.tsx';
// The SHARED panel and impact card — not module copies. Support at Home had hand-rolled its own
// Financial Impact block, which is why every improvement made to the shared one stopped at the
// Aged Care module (owner, 2026-08-27: make the modules consistent).
import PlanSummaryPanel from '../agedCare/PlanSummaryPanel.tsx';
import PlanExplanationModal from '../agedCare/PlanExplanationModal.tsx';
import {
    GiftingFieldNote,
    ReverseMortgageFieldNote,
    HeasFieldNote,
} from '../../components/strategy/StrategyFieldNotes.tsx';
import FinancialImpactPanel from '../agedCare/FinancialImpactPanel.tsx';
import SahStartingPosition from './SahStartingPosition.tsx';
import { simulateManualSahPlan } from './sahPlanFactFind.ts';
import { planRunwayCopy, summariseFunding } from '../../components/insights/fundingSummary.tsx';
import { buildMoneyStory, MoneyStoryBlock } from '../agedCare/moneyStory.tsx';
import { describeServicePlan, servicePlanBasisWarning, servicePlanDistribution, type ServicePlanBasis } from './servicePlanBasis.ts';
import { FUNERAL_BOND_LIMIT } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { JargonTerm } from '../../components/narrative/index.tsx';
import { PlainWordsCard, PlainWordsBeat } from '../../components/narrative/PlainWords.tsx';
import type { SupportAtHomeServicePlan } from '../../strategy/supportAtHomeEstimatedServicePlan.ts';
import { resolveServicePlanForSuggestionEngine, hasUsableServicePlan } from '../../strategy/supportAtHomeEstimatedServicePlan.ts';
import { SUPPORT_AT_HOME_SERVICES } from './supportAtHomeServices.tsx';
import { computeServiceCostsForPerson } from './serviceBuilderCosts.ts';
import {
    getSavedHomeCareStrategies,
    persistHomeCareStrategy,
    HOME_CARE_STRATEGIES_UPDATED_EVENT,
} from './homeCareStrategyStorage.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { SavedStrategy } from '../../strategy/StrategyIntegrationUtils.ts';
import type {
    SupportDownsizeAllocatorDetails,
    ReverseMortgageAllocatorDetails,
    HeasAllocatorDetails,
} from './SupportAtHomePropertyAllocators.tsx';
import type { RenovateDetails, UpgradeDetails } from './SupportAtHomeHelpers.tsx';
import type { DownsizeDetails as StrategyDownsizeDetails } from '../../strategy/supportAtHomeStrategyFunding.ts';

import { REVERSE_MORTGAGE_DEFAULT_RATE } from '../../../../shared/src/calculation/support/capitalisingLoans.ts';
import DebtsInPlanNote from '../../components/strategy/DebtsInPlanNote.tsx';
import { sahDebtClearedBy } from './sahDebts.ts';
import { EquityReleaseTerms } from '../../components/strategy/EquityReleaseTerms.tsx';
import { SAH_BENCH } from '../../utils/benchmarkWords.ts';
/** Manual Control's user-entered gifting/funeral-bond strategy state. */
interface ManualStrategiesDraft {
    gifting: number;
    funeralBonds: number;
    giftingSource: string;
    funeralSource: string;
    [key: string]: unknown;
}

/** Manual Control's overall strategy toggles (reverse mortgage / HEAS are mutually exclusive with each other). */
interface OverallStrategyDraft {
    reverseMortgage?: boolean;
    heas?: boolean;
    [key: string]: unknown;
}

/** Draft annuity configuration edited via SupportAtHomeAnnuityConfigurator, before buildAnnuityImpact resolves it.
 * `age` (the client's age) isn't part of AnnuityConfigurator's own AnnuityDetails shape (which only tracks
 * `partnerAge`) but is needed here to call the calc engine's buildAnnuityImpact. */
type AnnuityRecordDraft = AnnuityDetails & { age?: number | null };

/** Calc-engine `financials` result bag — only the fields this component reads off it are named here. */
interface SahFinancialsSnapshot {
    assessableAssets?: number;
    totalAssessableIncome?: number;
    socialSecurity?: number;
    socialSecurityBreakdown?: BreakdownRow[];
    cashFlow?: number;
    cashFlowBreakdown?: BreakdownRow[];
    netWorth?: number;
    [key: string]: unknown;
}

interface SahContributionRateSet {
    clinical?: number;
    independence?: number;
    everydayLiving?: number;
    [key: string]: unknown;
}

interface SahContributionBranch {
    contributionRates?: SahContributionRateSet;
    contributionAmounts?: { totalAnnual?: number };
    budget?: { total?: number };
    [key: string]: unknown;
}

interface SahContributionsResult {
    client?: SahContributionBranch;
    combined?: SahContributionBranch;
    partner?: SahContributionBranch;
    [key: string]: unknown;
}

/** One year of a projection (runProjections result row). */
interface SahProjectionYear {
    year?: number;
    cashFlow?: number;
    netWorth?: number;
    fyFullYearView?: unknown;
    [key: string]: unknown;
}

interface CombinedCareCosts {
    totalDailyCost: number;
    supportAtHomeDailyCost: number;
    agedCareDailyCost: number;
    supportAtHomeAnnualCost: number;
    agedCareAnnualCost: number;
    [key: string]: unknown;
}

/** factFind.servicePlan is a free-form bag (FactFind's `servicePlan?: Record<string, unknown>`) —
 * this narrower local shape covers the specific fields this component reads off it. */
interface SahServicePlanShape {
    serviceDistribution?: { clinical?: number; independence?: number; everyday?: number };
    services?: Record<string, unknown>;
    [key: string]: unknown;
}

/** Inputs bag of a previously-saved home_care strategy, as restored from sessionStorage to reopen
 * "Optimise Your Plan" pre-populated. Superset of SavedStrategy's free-form `inputs` (StrategyIntegrationUtils.ts). */
interface RestoredHomeCareStrategyInputs {
    gifting?: number | string;
    funeralBonds?: number | string;
    giftingSource?: string;
    funeralSource?: string;
    propertyStrategy?: string;
    annuityRecord?: AnnuityRecordDraft | null;
    overallStrategy?: OverallStrategyDraft;
    downsizeDetails?: SupportDownsizeAllocatorDetails;
    renovateDetails?: RenovateDetails;
    upgradeDetails?: UpgradeDetails;
    reverseMortgageDetails?: ReverseMortgageAllocatorDetails;
    heasDetails?: HeasAllocatorDetails;
    selectedAgedCareStrategyId?: string | null;
    selectedFundingStrategy?: string | null;
    [key: string]: unknown;
}

interface RestoredHomeCareStrategy {
    id?: string;
    inputs?: RestoredHomeCareStrategyInputs;
    [key: string]: unknown;
}

export interface StrategiesSectionProps {
    factFind: DraftFactFind;
    setFactFind: React.Dispatch<React.SetStateAction<DraftFactFind>>;
    onNext: () => void;
    onBack: () => void;
    strategyMode?: string;
    setStrategyMode?: (mode: string) => void;
    onNavigateToBuilder?: () => void;
    onNavigateToDashboard?: () => void;
    /**
     * When support services begin, owned by the page and SET in the Service Builder.
     *
     * A property of the service plan, not of the optimisation — but it is the date the Suggestion
     * Engine projects every candidate from, so this screen reads it. Lifted rather than duplicated:
     * two inputs for one fact is how a screen comes to disagree with the screen before it.
     */
    supportStartDate?: string;
    /**
     * Called with each generated Digital SOA (the strategy plus its printable snapshot) so it lands on
     * the server's advice register like aged care's (owner, 1 Oct 2026). App.tsx decides where.
     */
    onSoaGenerated?: (document: Record<string, unknown>) => void;
    /** Reopen a saved plan in place: the page remounts this section, which then restores it. */
    onReopen?: () => void;
    /** Inside the three steps (rebuild M3): the start date, starting position and service plan are steps 1–2. */
    stepped?: boolean;
}

/** Picker value for builder selections that were never saved as a named plan. */
const UNSAVED_PLAN_OPTION = '__unsaved__';

const StrategiesSection = ({ factFind, setFactFind, strategyMode = 'manual', setStrategyMode, onNavigateToBuilder, onNavigateToDashboard, supportStartDate = '', onSoaGenerated, onReopen, stepped = false }: StrategiesSectionProps) => {
    // Calculate participant type and other variables needed for contribution calculations
    const isPartnered = factFind?.adviceFor === 'couple';
    const defaultParticipantType = factFind?.careNeeds?.clientAcat?.status === 'yes' && factFind?.careNeeds?.clientAcat?.homeCareLevel
        ? (isPartnered ? 'couple' : 'single')
        : (isPartnered ? 'couple_partner' : 'single');
    const defaultNwopCategory = factFind?.careNeeds?.clientAcat?.nwopCategory || 'standard';
    const defaultPreviousITCF = factFind?.careNeeds?.clientAcat?.previousITCF || 0;
    
    const [participantType] = useState(defaultParticipantType);
    const [nwopCategory] = useState(defaultNwopCategory);
    const [meansDisclosed] = useState(true);
    const [previousITCF] = useState(defaultPreviousITCF);
    
    // Read restored strategy once at the top level (before useState calls)
    // Only restore if explicitly set (from Dashboard reopen), not from leftover sessionStorage
    const restoredStrategy: RestoredHomeCareStrategy | null = (() => {
        try {
            const stored = typeof window !== 'undefined' ? sessionStorage.getItem('careCompanionRestoreHomeCareStrategy') : null;
            if (stored) {
                const strategy = JSON.parse(stored);
                // Only restore if this is a saved strategy (has an id), not just leftover state
                // This prevents restoring unsaved strategies from previous sessions
                if (strategy.id) {
                    sessionStorage.removeItem('careCompanionRestoreHomeCareStrategy');
                    return strategy;
                } else {
                    // Clear leftover unsaved strategy data
                    sessionStorage.removeItem('careCompanionRestoreHomeCareStrategy');
                }
            }
        } catch (error) {
            console.error('Error restoring home care strategy:', error);
            // Clear corrupted data
            if (typeof window !== 'undefined') {
                sessionStorage.removeItem('careCompanionRestoreHomeCareStrategy');
            }
        }
        return null;
    })();
    
    // NO MODE CHOOSER (owner, 19 Oct 2026, the aged care ruling of 17 Oct): the tested plans are this
    // step and Manual Control is a link under them. A self-built plan reopened from Home goes straight
    // to Manual Control (its levers are what was reopened); everything else opens on the tested plans.
    const restoredIsManual = Boolean(restoredStrategy && (restoredStrategy as { source?: string }).source !== 'suggestion_engine');
    const [internalStrategyMode, setInternalStrategyMode] = useState<string>(
        restoredIsManual ? 'manual' : (strategyMode === 'manual' ? 'manual' : 'suggestion'),
    );
    const strategyModeSyncedRef = useRef(false);
    /** Which Support at Home suggestion-engine card is visually selected (matches Aged Care behaviour). */
    const [sahSeSelectedStrategy, setSahSeSelectedStrategy] = useState<Record<string, unknown> | null>(null);
    /** A just-saved manual strategy plus its projections as they stood at save time — the offer to
     * generate its Digital SOA (the save reset wipes the live state, so this is captured first). */
    const [manualSoaOffer, setManualSoaOffer] = useState<{ strategy: Record<string, unknown>; projections: Record<string, unknown>[] | null } | null>(null);
    /** "Maybe later" was chosen: the offer folds to one line instead of disappearing. */
    const [manualSoaDeferred, setManualSoaDeferred] = useState(false);

    /**
     * "Get the advice document" for the plan built by hand. The record captured when it was chosen,
     * or — on a later visit, when that capture is gone — the chosen record on file for these same
     * levers, with the projection on screen now (it is the same plan, priced the same way).
     */
    const openManualDigitalSoa = async () => {
        const fromFile = manualSoaOffer ? null : savedHomeCareStrategies.find(isChosenManualPlan);
        const offer = manualSoaOffer
            ?? (fromFile ? { strategy: fromFile as unknown as Record<string, unknown>, projections: ((projectionsForModal || projections) ?? null) as never } : null);
        if (!offer) return;
        const snapshot = await buildSupportAtHomeDigitalSOASnapshot({
            selectedStrategy: offer.strategy as never,
            factFind,
            projections: offer.projections as never,
            analysis: null,
            servicePlan: getServicePlanFromStorage() as never,
            strategySource: 'manual_control',
            // With the support alone, for the guaranteed income's "the same plan without it" column (sahGuaranteedIncome.ts).
            doNothingRows: (sahDoNothingRun.rows ?? null) as never,
            // A partner's aged care plan carried into the figures, said in the document (24 Oct 2026).
            linkedCare: selectedAgedCareStrategy ? await sahLinkedCareFor(factFind, selectedAgedCareStrategy) : null,
        });
        if (snapshot) {
            const document = { ...offer.strategy, soaSnapshot: snapshot };
            persistHomeCareStrategy(document as never);
            onSoaGenerated?.(document as Record<string, unknown>);
            openSupportAtHomeDigitalSOAAttachment(snapshot);
        }
        setManualSoaOffer(null);
    };
    /** When true in suggestion mode, unblur the right column and show fact-find baseline only (AC parity). */
    const [showBaseline, setShowBaseline] = useState(false);
    const [sahSeAnalyzing, setSahSeAnalyzing] = useState(false);
    const [savedHomeCareStrategies, setSavedHomeCareStrategies] = useState<SavedStrategy[]>(() => getSavedHomeCareStrategies());

    // Update internal state when prop changes
    // The first run tells the page which mode this mount opened in (a reopened self-built plan opens
    // Manual Control whatever the page held); later runs follow the page.
    useEffect(() => {
        if (!strategyModeSyncedRef.current) {
            strategyModeSyncedRef.current = true;
            if (setStrategyMode && strategyMode !== internalStrategyMode) setStrategyMode(internalStrategyMode);
            return;
        }
        if (strategyMode === 'manual' || strategyMode === 'suggestion') {
            setInternalStrategyMode(strategyMode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [strategyMode]);

    // Update parent when internal state changes (if setStrategyMode prop provided)
    const handleStrategyModeChange = (mode: string) => {
        setInternalStrategyMode(mode);
        if (mode === 'manual') {
            setSahSeSelectedStrategy(null);
            setShowBaseline(false);
            setSahSeAnalyzing(false);
        }
        if (setStrategyMode) {
            setStrategyMode(mode);
        }
    };

    useEffect(() => {
        const refreshSavedStrategies = () => setSavedHomeCareStrategies(getSavedHomeCareStrategies());
        refreshSavedStrategies();
        if (typeof window !== 'undefined') {
            window.addEventListener(HOME_CARE_STRATEGIES_UPDATED_EVENT, refreshSavedStrategies);
        }
        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener(HOME_CARE_STRATEGIES_UPDATED_EVENT, refreshSavedStrategies);
            }
        };
    }, []);
    
    const [manualStrategies, setManualStrategies] = useState<ManualStrategiesDraft>(() => {
        // Restore manual strategies from saved strategy
        if (restoredStrategy?.inputs) {
            return {
                gifting: Number(restoredStrategy.inputs.gifting) || 0,
                funeralBonds: Number(restoredStrategy.inputs.funeralBonds) || 0,
                giftingSource: restoredStrategy.inputs.giftingSource || 'bank',
                funeralSource: restoredStrategy.inputs.funeralSource || 'bank'
            };
        }
        return {
            gifting: 0,
            funeralBonds: 0,
            giftingSource: 'bank',
            funeralSource: 'bank'
        };
    });
    const [propertyStrategy, setPropertyStrategy] = useState<string>(restoredStrategy?.inputs?.propertyStrategy || 'none'); // 'none', 'downsize', 'renovate', 'upgrade', 'reverseMortgage', 'heas'
    const [annuityRecord, setAnnuityRecord] = useState<AnnuityRecordDraft | null>(restoredStrategy?.inputs?.annuityRecord || null);
    const [overallStrategy, setOverallStrategy] = useState<OverallStrategyDraft>(restoredStrategy?.inputs?.overallStrategy || {});
    const [downsizeDetails, setDownsizeDetails] = useState<SupportDownsizeAllocatorDetails>(() => {
        if (restoredStrategy?.inputs?.downsizeDetails) {
            return restoredStrategy.inputs.downsizeDetails;
        }
        return {
            salePrice: factFind?.home?.value || 0,
            sellingCosts: (Number(factFind?.home?.value) || 0) * 0.025,
            newHomePurchasePrice: 0,
            downsizerContribution: 0,
            partnerDownsizerContribution: 0,
            giftingAmount: 0,
            funeralBondPurchase: 0,
            addedAssets: []
        };
    });
    const [renovateDetails, setRenovateDetails] = useState<RenovateDetails>(() => {
        return restoredStrategy?.inputs?.renovateDetails || {
            renovationAmount: 0,
            fundingSources: []
        };
    });
    const [upgradeDetails, setUpgradeDetails] = useState<UpgradeDetails>(() => {
        if (restoredStrategy?.inputs?.upgradeDetails) {
            return restoredStrategy.inputs.upgradeDetails;
        }
        return {
            currentHomeValue: parseFloat(String(factFind.home?.value)) || 0,
            newHomePurchasePrice: 0,
            sellingCosts: 0,
            fundingSources: []
        };
    });
    const [reverseMortgageDetails, setReverseMortgageDetails] = useState<ReverseMortgageAllocatorDetails>(() => {
        return restoredStrategy?.inputs?.reverseMortgageDetails || {
            loanAmount: 0,
            loanPercentage: 0,
            interestRate: REVERSE_MORTGAGE_DEFAULT_RATE,
            loanStructure: 'lump_sum',
            regularPaymentAmount: 0,
            regularPaymentFrequency: 'fortnightly',
            establishmentFees: 0
        };
    });
    const [heasDetails, setHeasDetails] = useState<HeasAllocatorDetails>(() => {
        return restoredStrategy?.inputs?.heasDetails || {
            paymentAmount: 0,
            paymentFrequency: 'fortnightly',
            interestRate: 3.95
        };
    });
    const [availableAgedCareStrategies, setAvailableAgedCareStrategies] = useState<SavedStrategy[]>([]);
    const [selectedAgedCareStrategyId, setSelectedAgedCareStrategyId] = useState<string | null>(restoredStrategy?.inputs?.selectedAgedCareStrategyId || null);
    const [selectedFundingStrategy, setSelectedFundingStrategy] = useState<string | null>(restoredStrategy?.inputs?.selectedFundingStrategy || null);
    // 10 years, as in downsizing, granny flat, the village and cashflow (owner, 19 Oct 2026: "10 years
    // makes sense"; aged care alone keeps its age-and-health horizon, being the outlier on life expectancy).
    const [legacyYears, setLegacyYears] = useState(10);
    const [showProjectionsModal, setShowProjectionsModal] = useState(false);
    const [resetTrigger, setResetTrigger] = useState(0);
    const [showCompareModal, setShowCompareModal] = useState(false);
    const [optimiseStrategyName, setOptimiseStrategyName] = useState('');
    const [optimiseStrategyNameError, setOptimiseStrategyNameError] = useState('');

    // Load saved aged care strategies for integration
    useEffect(() => {
        const refreshStrategies = () => {
            try {
                const stored = typeof window !== 'undefined' ? sessionStorage.getItem('careCompanionStrategies') : null;
                if (!stored) {
                    setAvailableAgedCareStrategies([]);
                    return;
                }
                const parsed = JSON.parse(stored);
                const agedCareList: SavedStrategy[] = Array.isArray(parsed)
                    ? parsed.filter((strategy: SavedStrategy) => strategy?.type === 'aged_care')
                    : [];
                setAvailableAgedCareStrategies(agedCareList);
            } catch (error) {
                console.error('Error loading aged care strategies for integration:', error);
                setAvailableAgedCareStrategies([]);
            }
        };

        refreshStrategies();

        const handleStorage = (event: StorageEvent | undefined) => {
            if (!event || event.key === 'careCompanionStrategies') {
                refreshStrategies();
            }
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('storage', handleStorage);
        }

        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('storage', handleStorage);
            }
        };
    }, []);

    // Step 1's "Yes — X is moving into aged care" includes the chosen aged care plan here too, unless
    // a plan built by hand already says which (sahEngineContext.ts). "No" leaves it out.
    useEffect(() => {
        if (restoredStrategy) return;
        const answer = sahMoveAnswer(factFind);
        if (answer === 'yes' && !selectedAgedCareStrategyId) {
            const chosen = chosenAgedCarePlanOf(availableAgedCareStrategies);
            if (chosen?.id != null) setSelectedAgedCareStrategyId(chosen.id as string);
        } else if (answer !== 'yes' && selectedAgedCareStrategyId && answer != null) {
            setSelectedAgedCareStrategyId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableAgedCareStrategies, factFind?.goals?.sahAgedCareMove]);

    useEffect(() => {
        if (!selectedAgedCareStrategyId) return;
        const exists = availableAgedCareStrategies.some(strategy => strategy.id === selectedAgedCareStrategyId);
        if (!exists) {
            setSelectedAgedCareStrategyId(null);
        }
    }, [availableAgedCareStrategies, selectedAgedCareStrategyId]);

    const selectedAgedCareStrategy = useMemo(() => {
        if (!selectedAgedCareStrategyId) return null;
        return availableAgedCareStrategies.find(strategy => strategy.id === selectedAgedCareStrategyId) || null;
    }, [availableAgedCareStrategies, selectedAgedCareStrategyId]);

    const selectedAgedCareIntegration = useMemo(() => {
        return extractAgedCareIntegrationDetails(selectedAgedCareStrategy);
    }, [selectedAgedCareStrategy]);

    /*
     * THE WHOLE AGED CARE PLAN, here as in the engine (owner, 24 Oct 2026; Manual Control parity). The
     * household a hand-built plan starts from is the linked aged care plan carried out — every piece of it,
     * its living costs — and the plan's own levers go on top. The deposit is then not paid a second time.
     * When the plan cannot be rebuilt, the deposit alone is applied, as before.
     */
    const linkedPosition = useSahLinkedPosition(factFind, selectedAgedCareStrategy);
    /*
     * WORKING IT OUT (25 Oct 2026): the rail's figures come from a chain of calculations — the household after the
     * aged care plan, the position, the contributions, the projections and "doing nothing" beside them. While any is
     * in flight the rail says so instead of mixing new figures with old ones (utils/calcTracker.ts).
     */
    const { track: trackSah, working: sahCalcWorking } = useCalcTracker();
    const sahRailWorking = sahCalcWorking || !linkedPosition.ready;
    const householdFactFind = useMemo<DraftFactFind>(
        () => (selectedAgedCareIntegration && linkedPosition.positioned ? sahBaselineFactFind(factFind, selectedAgedCareIntegration, undefined, linkedPosition.positioned) : factFind),
        [factFind, selectedAgedCareIntegration, linkedPosition.positioned],
    );
    const integrationForPlan = useMemo(
        () => (selectedAgedCareIntegration && linkedPosition.positioned ? { ...selectedAgedCareIntegration, radFunding: null } : selectedAgedCareIntegration),
        [selectedAgedCareIntegration, linkedPosition.positioned],
    );

    const agedCareResidencyOverrides = useMemo(() => {
        if (!selectedAgedCareIntegration) return {};
        const fromLinkedStrategy = selectedAgedCareIntegration?.strategyOptions || {};
        const linkedOverrides = {
            ...(fromLinkedStrategy?.clientResidencyOverride ? { clientResidencyOverride: fromLinkedStrategy.clientResidencyOverride } : {}),
            ...(fromLinkedStrategy?.partnerResidencyOverride ? { partnerResidencyOverride: fromLinkedStrategy.partnerResidencyOverride } : {}),
        };
        if (Object.keys(linkedOverrides).length > 0) return linkedOverrides;
        return getAgedCareModellerResidencyOverrides(factFind as unknown as FactFind);
    }, [selectedAgedCareIntegration, factFind]);

    /*
     * Who moves into care under the linked plan, and whether that makes the household an
     * illness-separated couple — derived from the SAME residency overrides the calculations run
     * with, so the words can never disagree with the figures (the aged care module's frame rule).
     */
    const integrationSummary = useMemo<SahIntegrationSummary | null>(() => {
        if (!selectedAgedCareIntegration) return null;
        const overrides = (selectedAgedCareIntegration?.strategyOptions || {}) as {
            clientResidencyOverride?: string;
            partnerResidencyOverride?: string;
        };
        const partnerInCare = overrides.partnerResidencyOverride === 'aged_care';
        const clientInCare = overrides.clientResidencyOverride === 'aged_care' || !partnerInCare;
        const personName =
            (clientInCare ? factFind?.client?.firstName : factFind?.partner?.firstName) ||
            (clientInCare ? 'the client' : 'their partner');
        const illnessSeparated =
            factFind?.adviceFor === 'couple' &&
            isIllnessSeparatedCouple(factFind as unknown as FactFind, agedCareResidencyOverrides as never);
        return {
            name: String(selectedAgedCareStrategy?.name || 'aged care plan'),
            personName,
            annualFees: Number(selectedAgedCareIntegration.annualCost) || 0,
            radPaid: Number(selectedAgedCareIntegration.radPaid) || 0,
            illnessSeparated,
        };
    }, [selectedAgedCareIntegration, selectedAgedCareStrategy, factFind, agedCareResidencyOverrides]);

    
    // Save selected strategy to sessionStorage so ServiceBuilderSection can access it
    useEffect(() => {
        if (selectedAgedCareStrategy && typeof window !== 'undefined') {
            sessionStorage.setItem('careCompanionSelectedAgedCareStrategy', JSON.stringify(selectedAgedCareStrategy));
        } else if (!selectedAgedCareStrategy && typeof window !== 'undefined') {
            sessionStorage.removeItem('careCompanionSelectedAgedCareStrategy');
        }
    }, [selectedAgedCareStrategy]);
    
    const hasAgedCareStrategies = availableAgedCareStrategies.length > 0;
    
    // Calculate available annuity funding sources
    const availableAnnuityFundingSources = useMemo<AnnuityFundingSource[]>(() => {
        const sources: AnnuityFundingSource[] = [];
        const formatter = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
        const clientName = factFind?.client?.firstName || 'Client';
        const partnerName = factFind?.partner?.firstName || 'Partner';

        const ownershipLabel = (ownership: string | undefined): string => {
            if (!ownership) return '';
            if (ownership === 'joint') return 'Joint';
            if (ownership === 'partner') return partnerName;
            return clientName;
        };

        const pushSource = (type: string, asset: { id?: string | number; accountId?: string | number; ownership?: string } | undefined, amount: number | string | undefined, fallbackLabel: string) => {
            const numericAmount = parseFloat(String(amount)) || 0;
            if (numericAmount <= 0) return; // Only include sources with value
            const ownership = asset?.ownership || (factFind?.adviceFor === 'couple' ? 'joint' : 'client');
            const label = `${fallbackLabel}${ownership ? ` (${ownershipLabel(ownership)})` : ''} – ${formatter.format(numericAmount)}`;
            sources.push({
                id: asset?.id ? `${type}-${asset.id}` : `${type}-${fallbackLabel}`,
                type,
                assetId: asset?.id ?? asset?.accountId ?? null,
                label,
                ownership
            });
        };

        // Use optimized factFind that includes property strategy + aged care integration changes (the
        // whole linked aged care plan, when it could be rebuilt).
        let formDataForFunding: DraftFactFind = householdFactFind;

        // Apply property strategies to factFind for funding source calculation
        if (propertyStrategy === 'downsize' && Number(downsizeDetails.salePrice) > 0) {
            const netProceeds = (parseFloat(String(downsizeDetails.salePrice)) || 0) - (parseFloat(String(downsizeDetails.sellingCosts)) || 0);
            const totalAllocated = (parseFloat(String(downsizeDetails.newHomePurchasePrice)) || 0) +
                (parseFloat(String(downsizeDetails.downsizerContribution)) || 0) +
                (parseFloat(String(downsizeDetails.partnerDownsizerContribution)) || 0) +
                (parseFloat(String(downsizeDetails.giftingAmount)) || 0) +
                (parseFloat(String(downsizeDetails.debtRepayment)) || 0);
            const unallocatedProceeds = netProceeds - totalAllocated;
            if (unallocatedProceeds > 0) {
                sources.push({
                    id: 'downsize-proceeds',
                    type: 'bank',
                    assetId: null,
                    label: `Home Sale Proceeds – ${formatter.format(unallocatedProceeds)}`,
                    ownership: factFind?.adviceFor === 'couple' ? 'joint' : 'client'
                });
            }
        }

        // Apply linked aged care strategy funding impacts (e.g. RAD payments)
        if (integrationForPlan?.radFunding) {
            try {
                formDataForFunding = applyRadFundingToFactFind(formDataForFunding, integrationForPlan.radFunding);
            } catch (error) {
                console.error('Error applying aged care RAD funding to annuity sources:', error);
            }
        }

        (formDataForFunding.assets?.accountBasedPensions || []).forEach(pension => {
            const amount = pension.value ?? pension.balance ?? 0;
            pushSource('abp', pension, amount, pension.description || 'Account-based pension');
        });

        (formDataForFunding.assets?.superannuation || []).forEach(superItem => {
            pushSource('super', superItem, superItem.value, superItem.description || 'Superannuation');
        });

        (formDataForFunding.assets?.shares || []).forEach(share => {
            pushSource('shares', share, share.value, share.description || 'Shares / managed fund');
        });

        (formDataForFunding.assets?.bankAccounts || []).forEach(account => {
            pushSource('bank', account, account.value, account.description || 'Bank account');
        });

        return sources;
    }, [factFind, factFind?.adviceFor, factFind?.client?.firstName, factFind?.partner?.firstName, propertyStrategy, downsizeDetails, selectedAgedCareIntegration, householdFactFind, integrationForPlan]);
    
    // Initialize annuity record with proper structure
    const initializeAnnuityRecord = (): AnnuityRecordDraft => {
        const isCouple = factFind?.adviceFor === 'couple';
        const owner = isCouple ? 'joint' : 'client';
        return {
            /*
             * A NEW GUARANTEED INCOME STARTS AS THE FULL AMOUNT BACK WHERE THE PERSON MAY BUY IT (owner, 21 Oct
             * 2026 — the guaranteed income review M1, mock-up screen 6: "Starts as full amount back where
             * eligible"). People receiving support at home are; otherwise money back. Never income only.
             */
            annuityType: guaranteedShapeAvailable(factFind as never, owner) ? 'guaranteed' : 'lifetime',
            purchaseAmount: 0,
            // See DEFAULT_NEW_ANNUITY_RESIDUAL — capital preserved unless the client chooses otherwise.
            residualCapital: DEFAULT_NEW_ANNUITY_RESIDUAL,
            owner,
            fundingSource: {
                id: 'bank-default',
                type: 'bank',
                assetId: null,
                label: 'Bank accounts / cash',
                ownership: isCouple ? 'joint' : 'client'
            },
            indexation: false,
            reversionary: false,
            age: factFind?.client?.dob ? calculateAge(factFind.client.dob) : null,
            partnerAge: isCouple && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null
        };
    };

    // Calculate annuity record for financial impact (from annuityDetails state). buildAnnuityImpact is the
    // real (strict) calc-engine function; the Draft factFind cast is safe (see factFindTypes.ts header comment).
    const annuityRecordForCalculation: Record<string, unknown> | null = useMemo(() => {
        if (!annuityRecord || !annuityRecord.purchaseAmount || Number(annuityRecord.purchaseAmount) <= 0) return null;
        try {
            return buildAnnuityImpact(factFind as unknown as FactFind, annuityRecord);
        } catch (error) {
            console.error('Error building annuity impact', error);
            return null;
        }
    }, [annuityRecord, factFind]);

    // Calculate annuity summary for display
    const annuitySummary = useMemo(() => {
        if (!annuityRecordForCalculation) return null;
        return summariseAnnuityImpact(annuityRecordForCalculation);
    }, [annuityRecordForCalculation]);

    // Calculate annuity warnings
    const annuityWarnings = useMemo<string[]>(() => {
        if (!annuityRecord || !annuityRecord.purchaseAmount || Number(annuityRecord.purchaseAmount) <= 0) return [];
        const warnings: string[] = [];
        const purchaseAmount = parseFloat(String(annuityRecord.purchaseAmount)) || 0;

        const selectedSource = availableAnnuityFundingSources.find(src => src.id === annuityRecord.fundingSource?.id);
        const sourceType = selectedSource?.type || annuityRecord.fundingSource?.type;

        const balanceForSource = (): number => {
            if (!sourceType) return Infinity;
            const matchId = selectedSource?.assetId;
            const totalOrMatch = (list: { id?: string | number; accountId?: string | number; value?: number; balance?: number }[] | undefined): number => {
                if (!Array.isArray(list)) return 0;
                if (matchId === null || matchId === undefined) {
                    return list.reduce((sum, item) => sum + (item.value ?? item.balance ?? 0), 0);
                }
                const match = list.find(item => String(item.id ?? item.accountId) === String(matchId));
                return match ? (match.value ?? match.balance ?? 0) : 0;
            };

            if (sourceType === 'bank') return totalOrMatch(factFind.assets?.bankAccounts);
            if (sourceType === 'shares') return totalOrMatch(factFind.assets?.shares);
            if (sourceType === 'abp') return totalOrMatch(factFind.assets?.accountBasedPensions);
            if (sourceType === 'super') return totalOrMatch(factFind.assets?.superannuation);
            return Infinity;
        };

        const availableBalance = balanceForSource();
        if (purchaseAmount > availableBalance) {
            warnings.push('Purchase amount exceeds the available balance in the selected funding source.');
        }

        if (!selectedSource) {
            warnings.push('Please confirm a funding source for the annuity.');
        }

        return warnings;
    }, [annuityRecord, availableAnnuityFundingSources, factFind.assets]);

    // Update annuityRecord funding source when available sources change
    useEffect(() => {
        if (!annuityRecord || availableAnnuityFundingSources.length === 0) return;

        const fallbackSource: AnnuityFundingSource = availableAnnuityFundingSources[0] || {
            id: 'bank-default',
            type: 'bank',
            assetId: null,
            label: 'Bank accounts / cash',
            ownership: factFind?.adviceFor === 'couple' ? 'joint' : 'client'
        };

        if (!annuityRecord.fundingSource || !availableAnnuityFundingSources.some(src => src.id === annuityRecord.fundingSource?.id)) {
            setAnnuityRecord(prev => (prev ? {
                ...prev,
                fundingSource: fallbackSource
            } : prev));
        }
    }, [availableAnnuityFundingSources, annuityRecord, factFind?.adviceFor]);
    
    // Update overallStrategy when propertyStrategy changes
    useEffect(() => {
        if (propertyStrategy === 'reverseMortgage') {
            setOverallStrategy(prev => ({ ...prev, reverseMortgage: true, heas: false }));
        } else if (propertyStrategy === 'heas') {
            setOverallStrategy(prev => ({ ...prev, reverseMortgage: false, heas: true }));
        } else {
            setOverallStrategy(prev => ({ ...prev, reverseMortgage: false, heas: false }));
        }
    }, [propertyStrategy]);
    
    // Reset function to clear all strategies after saving. `keepLinkedCare` for a blank plan opened by
    // "Build a plan yourself" or "Clear and start again" (25 Oct 2026): the household stays as it is.
    const resetStrategiesToBaseline = (keepLinkedCare = false) => {
        
        // Clear any leftover restore strategy from sessionStorage
        if (typeof window !== 'undefined') {
            sessionStorage.removeItem('careCompanionRestoreHomeCareStrategy');
        }
        
        // Reset manual strategies
        setManualStrategies({
            gifting: 0,
            funeralBonds: 0,
            giftingSource: 'bank',
            funeralSource: 'bank'
        });
        
        // Reset property strategy
        setPropertyStrategy('none');
        
        // Reset property strategy details
        setDownsizeDetails({
            salePrice: factFind?.home?.value || 0,
            sellingCosts: (Number(factFind?.home?.value) || 0) * 0.025,
            newHomePurchasePrice: 0,
            downsizerContribution: 0,
            partnerDownsizerContribution: 0,
            giftingAmount: 0,
            funeralBondPurchase: 0,
            addedAssets: []
        });
        setRenovateDetails({
            renovationAmount: 0,
            fundingSources: []
        });
        setUpgradeDetails({
            currentHomeValue: parseFloat(String(factFind?.home?.value)) || 0,
            newHomePurchasePrice: 0,
            sellingCosts: 0,
            fundingSources: []
        });
        setReverseMortgageDetails({
            loanAmount: 0,
            loanPercentage: 0,
            interestRate: REVERSE_MORTGAGE_DEFAULT_RATE,
            loanStructure: 'lump_sum',
            regularPaymentAmount: 0,
            regularPaymentFrequency: 'fortnightly',
            establishmentFees: 0
        });
        setHeasDetails({
            paymentAmount: 0,
            paymentFrequency: 'fortnightly',
            interestRate: 3.95
        });
        
        // Reset overall strategy flags
        setOverallStrategy({});
        
        // Reset funding strategies
        setSelectedFundingStrategy(null);
        setAnnuityRecord(null);
        
        // Reset aged care integration
        if (!keepLinkedCare) setSelectedAgedCareStrategyId(null);
        setManualSeededFrom(null);
    };
    
    // Trigger reset when resetTrigger changes
    useEffect(() => {
        if (resetTrigger > 0) {
            resetStrategiesToBaseline();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetTrigger]);
    
    // Calculate BEFORE baseline (original position) — with a partner moving into care, the household once
    // the aged care plan is carried out, on its residency and with its fees, as the engine's "doing
    // nothing" is (24 Oct 2026), so a hand-built plan is measured from the same place.
    const [beforeFinancials, setBeforeFinancials] = useState<SahFinancialsSnapshot | null>(null);
    useEffect(() => {
        if (!factFind) { setBeforeFinancials(null); return undefined; }
        let cancelled = false;
        const linkedBefore = linkedPosition.positioned && selectedAgedCareIntegration
            ? { ...agedCareResidencyOverrides, integratedAgedCareCost: Number(selectedAgedCareIntegration.annualCost) || 0, integratedAgedCareDetails: integrationForPlan }
            : undefined;
        trackSah(calculationApi.calculateFinancials(householdFactFind, linkedBefore)).then((r) => { if (!cancelled) setBeforeFinancials(r); });
        return () => { cancelled = true; };
    }, [factFind, householdFactFind, linkedPosition.positioned, selectedAgedCareIntegration, agedCareResidencyOverrides, integrationForPlan]);



    /*
     * ONE service plan for BOTH modelling modes (owner-caught asymmetry, 2026-08-25): the
     * Suggestion Engine resolved a plan (Service Builder's current plan, else the newest saved
     * one, else an illustrative package) while Manual Control silently read only
     * factFind.servicePlan — and modelled ZERO service costs when it was empty. Both modes now
     * consume this resolution, and the case line above the mode chooser SAYS which plan is in
     * use, so building the service plan first or the strategy first both read sensibly. The
     * sections unmount on tab switch, so a mount-time read of storage is always current.
     */
    /** Bumped when a plan is picked in-place below, so the resolution re-reads storage. */
    const [planPickVersion, setPlanPickVersion] = useState(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const allSavedServicePlans = useMemo(() => listSavedServicePlans(), [planPickVersion]);
    // Only plans with services are offered — an empty one resolves to "no plan", so picking it
    // snapped the control straight back (owner-caught, 2026-09-13). The empty ones are named below.
    const savedServicePlans = useMemo(() => usableSavedServicePlans(allSavedServicePlans), [allSavedServicePlans]);
    const emptySavedServicePlans = useMemo(
        () => allSavedServicePlans.filter((p) => !savedServicePlans.includes(p)),
        [allSavedServicePlans, savedServicePlans],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const servicePlanResolution = useMemo<{ plan: SupportAtHomeServicePlan | null; origin: ServicePlanSource }>(() => {
        const stored = getServicePlanWithSource();
        if (hasUsableServicePlan(stored.plan as unknown as SupportAtHomeServicePlan)) {
            return { plan: stored.plan as unknown as SupportAtHomeServicePlan, origin: stored.source };
        }
        /*
         * "NO SERVICE PLAN" MEANS NO SERVICE PLAN (owner-caught twice, 2026-08-27 and 2026-09-24).
         * Picking it cleared the session's current plan — and this resolution then fell through to
         * the newest saved plan, and failing that to the plan saved on the profile, so the picker
         * snapped back to "Service Plan A" the moment it was let go. The choice is recorded now
         * (`chosen`) and honoured here, before anything is restored. sahOptimiseServicePlanChoice
         * .test.ts and servicePlanStorage.test.ts hold it.
         */
        if (stored.source.kind === 'none' && stored.source.chosen) {
            return { plan: null, origin: stored.source };
        }
        const fromFactFind = factFind?.servicePlan as unknown as SupportAtHomeServicePlan | undefined;
        if (hasUsableServicePlan(fromFactFind)) {
            return { plan: fromFactFind as SupportAtHomeServicePlan, origin: { kind: 'restore', name: 'the plan saved on the profile' } };
        }
        return { plan: null, origin: { kind: 'none' } };
    }, [factFind, planPickVersion]);
    // With "Yes — X is moving into aged care" (step 1), X has no Support at home services priced.
    const servicePlanSource = useMemo(
        () => (servicePlanResolution.plan ? sahPricedServicePlan(servicePlanResolution.plan, factFind) : servicePlanResolution.plan),
        [servicePlanResolution, factFind],
    );
    const resolvedServicePlan = useMemo(
        () => resolveServicePlanForSuggestionEngine(servicePlanSource, isPartnered),
        [servicePlanSource, isPartnered],
    );


    /**
     * Which entry the service-plan picker should be showing.
     *
     * Derived from the resolution rather than held as its own state: the plan can change from the
     * Service Builder, from a restore, or from the picker itself, and a control with its own idea of
     * the answer would drift from the sentence beside it — which is exactly what it did while pinned
     * to "choose…".
     */
    const selectedSavedPlanId = useMemo(() => {
        if (resolvedServicePlan.isEstimated) return '';
        const origin = servicePlanResolution.origin;
        const name =
            origin.kind === 'saved' ? origin.name
            : origin.kind === 'current' ? origin.basedOnName
            : origin.kind === 'restore' ? origin.name
            : undefined;
        const match = name ? savedServicePlans.find((p) => p.name === name) : undefined;
        // A real plan with no saved counterpart is unsaved builder selections, not "none".
        return match ? match.id : UNSAVED_PLAN_OPTION;
    }, [resolvedServicePlan, servicePlanResolution, savedServicePlans]);

    /**
     * The resolved plan WITH its real category split attached.
     *
     * supportAtHomeContributionEngine reads `serviceDistribution` to decide what each contribution
     * rate is charged on, and assumes 40/35/25 of the whole budget when there isn't one. The plan
     * this screen hands it is `{ client, partner }` — neither `serviceDistribution` nor `services` —
     * so that fallback fired even for a real, built plan, and Optimise quoted the assumed split
     * while the Service Builder priced the actual services. One plan, two answers (owner-caught,
     * 2026-08-27: $7,722 here against $0 there).
     *
     * Every call that prices contributions goes through this, so the two screens cannot disagree.
     */
    const planForPricing = useMemo(
        () => ({
            ...(resolvedServicePlan.plan as Record<string, unknown>),
            serviceDistribution: servicePlanDistribution(resolvedServicePlan.plan as never),
        }),
        [resolvedServicePlan],
    );

    /**
     * The plan a strategy saved RIGHT NOW would be priced against.
     *
     * Stamped onto every saved strategy so a later change of services can be detected. A Support at
     * Home strategy's dollars are `budget x rate`: optimising moves the rate, the Service Builder
     * moves the budget, so the rates survive a service change and the dollars do not.
     */
    const currentServicePlanBasis = useMemo(
        () => describeServicePlan(resolvedServicePlan.plan as never, resolvedServicePlan.isEstimated),
        [resolvedServicePlan],
    );

    /** How big the real plan is — so the case line can IDENTIFY it, not just gesture at it. */
    const servicePlanStats = useMemo(() => {
        if (resolvedServicePlan.isEstimated) return null;
        const count = (side: Record<string, { quantity?: number | string } | undefined> | undefined) =>
            Object.values(side || {}).filter((d) => d && Number(d.quantity) > 0).length;
        const value = (side: Record<string, { quantity?: number | string; frequency?: string } | undefined> | undefined) =>
            computeServiceCostsForPerson(side || {}, {}, SUPPORT_AT_HOME_SERVICES).total;
        return {
            serviceCount: count(resolvedServicePlan.plan.client) + count(resolvedServicePlan.plan.partner),
            annualValue: value(resolvedServicePlan.plan.client) + value(resolvedServicePlan.plan.partner),
        };
    }, [resolvedServicePlan]);

    const [beforeContributions, setBeforeContributions] = useState<SahContributionsResult | null>(null);
    useEffect(() => {
        if (!beforeFinancials) { setBeforeContributions(null); return undefined; }
        let cancelled = false;
        // Use the same participant type settings as the after calculation
        // The resolved plan (real or illustrative) rides on the fact-find copy, so before and
        // after are priced on the SAME services.
        const beforeFactFind = { ...householdFactFind, servicePlan: planForPricing as never };
        // On the residency the after call and the engine's baseline use (illness-separated with a partner
        // in care), so a plan that changes nothing is priced at the same rates as doing nothing.
        trackSah(calculationApi.calculateIndividualContributions(beforeFactFind, beforeFinancials, participantType, nwopCategory, meansDisclosed, Number(previousITCF) || 0, 0.5, { strategyOptions: agedCareResidencyOverrides })).then((r) => {
            if (!cancelled) setBeforeContributions(r);
        });
        return () => { cancelled = true; };
    }, [beforeFinancials, factFind, householdFactFind, participantType, nwopCategory, meansDisclosed, previousITCF, resolvedServicePlan, agedCareResidencyOverrides]);

    const beforeContributionData = isPartnered
        ? (beforeContributions?.combined || beforeContributions?.client)
        : beforeContributions?.client;

    // Calculate optimized scenario with all strategies applied (sahPlanFactFind.ts)
    const optimizedScenario = useMemo(() => {
        if (!factFind) return null;
        return simulateManualSahPlan(householdFactFind, {
            manualStrategies, propertyStrategy, downsizeDetails, renovateDetails, upgradeDetails, overallStrategy,
            reverseMortgageDetails, selectedAgedCareIntegration: integrationForPlan, selectedFundingStrategy, annuityRecordForCalculation, beforeFinancials,
        });
    }, [factFind, householdFactFind, manualStrategies, propertyStrategy, downsizeDetails, renovateDetails, upgradeDetails, overallStrategy, reverseMortgageDetails, integrationForPlan, selectedFundingStrategy, annuityRecordForCalculation, beforeFinancials]);

    const [afterFinancials, setAfterFinancials] = useState<SahFinancialsSnapshot | null>(null);
    useEffect(() => {
        if (!optimizedScenario?.optimizedFactFind) { setAfterFinancials(null); return undefined; }

        const { optimizedFactFind, agedCareAnnualCost } = optimizedScenario;

        const strategyOptionsPayload: Record<string, unknown> = {
            reverseMortgage: overallStrategy.reverseMortgage,
            reverseMortgageDetails: overallStrategy.reverseMortgage ? reverseMortgageDetails : null,
            heas: overallStrategy.heas,
            heasDetails: overallStrategy.heas ? heasDetails : null,
            integratedAgedCareCost: agedCareAnnualCost,
            integratedAgedCareDetails: selectedAgedCareIntegration && agedCareAnnualCost > 0 ? selectedAgedCareIntegration : null,
            ...agedCareResidencyOverrides
        };

        if (selectedFundingStrategy === 'annuity' && annuityRecordForCalculation) {
            strategyOptionsPayload.annuities = [annuityRecordForCalculation];
        }

        let cancelled = false;
        trackSah(calculationApi.calculateFinancials(optimizedFactFind, strategyOptionsPayload)).then((r) => {
            if (!cancelled) setAfterFinancials(r);
        });
        return () => { cancelled = true; };
    }, [optimizedScenario, overallStrategy, reverseMortgageDetails, heasDetails, selectedAgedCareIntegration, selectedFundingStrategy, annuityRecordForCalculation, agedCareResidencyOverrides]);
    
    // Save optimized financials to sessionStorage only in manual mode (avoid SE/manual cross-leak to Service Builder)
    useEffect(() => {
        if (internalStrategyMode !== 'manual') return;
        if (typeof window !== 'undefined' && afterFinancials) {
            try {
                sessionStorage.setItem('careCompanionOptimizedFinancials', JSON.stringify({
                    financials: afterFinancials,
                    timestamp: Date.now()
                }));
                // Dispatch custom event to notify other components (same tab) - use setTimeout to avoid render issues
                setTimeout(() => {
                    window.dispatchEvent(new Event('optimizedFinancialsUpdated'));
                }, 0);
            } catch (error) {
                console.error('Error saving optimized financials:', error);
            }
        } else if (typeof window !== 'undefined' && !afterFinancials) {
            // Clear if no strategies applied
            try {
                sessionStorage.removeItem('careCompanionOptimizedFinancials');
                setTimeout(() => {
                    window.dispatchEvent(new Event('optimizedFinancialsUpdated'));
                }, 0);
            } catch (error) {
                console.error('Error clearing optimized financials:', error);
            }
        }
    }, [afterFinancials, internalStrategyMode]);
    
    /**
     * Which means test sets the Age Pension today — 'assets', 'income', or null when neither reduces it.
     * The questions group the levers by it (decision 8: every lever stays ticked; the ones that work on
     * the binding test lead). The same computation as the starting position card.
     */
    const sahBindingTest = useMemo<'assets' | 'income' | null>(() => {
        if (!beforeFinancials) return null;
        const data = calculateSahOptimizationTargets({
            assessableAssets: Number(beforeFinancials.assessableAssets) || 0,
            assessableIncome: Number(beforeFinancials.totalAssessableIncome) || 0,
            currentPension: Number(beforeFinancials.socialSecurity) || 0,
            isCouple: isPartnered,
            isHomeowner: anyoneOwnsHome(factFind as unknown as FactFind),
            illnessSeparatedForPension: resolveIllnessSeparatedForSahInsight(factFind, beforeFinancials as never, undefined, false),
        });
        if (Math.abs(data.pensionFromAssetTest - data.pensionFromIncomeTest) < 1) return null;
        return data.isAssetTestLimiting ? 'assets' : 'income';
    }, [beforeFinancials, factFind, isPartnered]);

    /** The tested plan Manual Control was opened from, named in the banner ("Started from …"). */
    const [manualSeededFrom, setManualSeededFrom] = useState<string | null>(null);

    /**
     * "Build a plan yourself" / "Customise this plan" (owner, 19 Oct 2026: Manual Control shares the
     * engine's look and feel; aged care made "your selection carries over" true on 17 Oct; the two doors
     * named 25 Oct 2026). Opens Manual Control with the levers set to the plan's pieces — the amounts
     * exactly, the money taken from the same kind of account. With no plan, it opens BLANK: every piece
     * cleared first, as after a save, so a plan customised earlier never reappears.
     */
    const buildFromEnginePlan = (row: Record<string, unknown> | null) => {
        resetStrategiesToBaseline(true);
        const config = (row?.config ?? null) as Record<string, unknown> | null;
        const steps = config
            ? (config.type === 'combination' && Array.isArray(config.steps) ? config.steps as Record<string, unknown>[] : [config])
            : [];
        const sourceKind = (step: Record<string, unknown>): string => {
            const sources = Array.isArray(step.fundingSources) ? step.fundingSources as Array<{ type?: string; amountToTake?: number }> : [];
            const lead = [...sources].sort((a, b) => (Number(b.amountToTake) || 0) - (Number(a.amountToTake) || 0))[0];
            const type = String(lead?.type ?? step.assetType ?? 'bankAccounts');
            return type === 'accountBasedPensions' ? 'abp' : type === 'shares' ? 'shares' : 'bank';
        };
        const fundingAsAllocations = (step: Record<string, unknown>) =>
            (Array.isArray(step.fundingSources) ? step.fundingSources as Array<{ id?: string | number; type?: string; amountToTake?: number }> : [])
                .filter((s) => s.id != null && s.type && (Number(s.amountToTake) || 0) > 0)
                .map((s) => ({ assetId: s.id as string | number, assetType: String(s.type), amount: Math.round(Number(s.amountToTake) || 0) }));

        const nextManual: ManualStrategiesDraft = { gifting: 0, funeralBonds: 0, giftingSource: 'bank', funeralSource: 'bank' };
        let nextProperty = 'none';
        let nextAnnuity: AnnuityRecordDraft | null = null;
        const nextDebt: Record<string, number> = {};
        const nextDebtFunding: Record<string, number> = {};
        for (const step of steps) {
            const t = step.type;
            if (t === 'gifting') { nextManual.gifting = Number(step.amount) || 0; nextManual.giftingSource = sourceKind(step); }
            else if (t === 'funeralBonds') { nextManual.funeralBonds = Number(step.amount) || 0; nextManual.funeralSource = sourceKind(step); }
            else if (t === 'annuity') {
                // The plan's own type (25 Oct 2026): a new record starts as the full-amount-back type where the
                // person may buy it, so a tested nil-residual income came across as the guaranteed shape.
                const lead = Array.isArray(step.fundingSources) ? [...(step.fundingSources as Array<{ id?: unknown; type?: unknown; amountToTake?: unknown }>)].sort((a, b) => (Number(b.amountToTake) || 0) - (Number(a.amountToTake) || 0))[0] : null;
                // The picker's own kinds and ids (`bank-<id>`), so the source is not swapped for its first one.
                const rawType = String(lead?.type ?? step.assetType ?? '');
                const srcType = ({ bankAccounts: 'bank', termDeposits: 'bank', accountBasedPensions: 'abp', superannuation: 'super', shares: 'shares', managedFunds: 'shares' } as Record<string, string>)[rawType] ?? rawType;
                const srcId = lead?.id ?? step.assetId;
                const base = initializeAnnuityRecord();
                nextAnnuity = {
                    ...base,
                    annuityType: step.annuityType === 'guaranteed' ? 'guaranteed' : 'lifetime',
                    purchaseAmount: Number(step.purchaseAmount) || 0,
                    residualCapital: step.annuityType === 'guaranteed' ? 1 : Number(step.residualCapital) || 0,
                    indexation: step.indexation === true,
                    ...(step.owner === 'client' || step.owner === 'partner' || step.owner === 'joint' ? { owner: step.owner } : {}),
                    ...(srcType && srcId != null ? { fundingSource: { id: `${srcType}-${String(srcId)}`, type: srcType, assetId: srcId as string | number, label: srcType, ownership: (base.fundingSource as { ownership?: string } | undefined)?.ownership ?? 'client' } } : {}),
                };
            } else if (t === 'increaseHomeEquity') {
                nextProperty = 'renovate';
                setRenovateDetails({ renovationAmount: Number(step.equityAmount ?? step.renovationAmount) || 0, fundingSources: fundingAsAllocations(step) });
            } else if (t === 'downsize') {
                nextProperty = 'downsize';
                if (step.downsizeDetails) setDownsizeDetails(step.downsizeDetails as SupportDownsizeAllocatorDetails);
            } else if (t === 'reverseMortgage') {
                nextProperty = 'reverseMortgage';
                setReverseMortgageDetails((prev) => ({
                    ...prev,
                    loanAmount: Number(step.loanAmount) || 0,
                    loanPercentage: Number(step.loanPercentage) || 0,
                    interestRate: Number(step.interestRate) || prev.interestRate,
                    loanStructure: 'lump_sum',
                }));
            } else if (t === 'heas') {
                nextProperty = 'heas';
                setHeasDetails((prev) => ({
                    ...prev,
                    paymentAmount: Number(step.paymentAmount) || 0,
                    paymentFrequency: String(step.paymentFrequency || 'fortnightly') as HeasAllocatorDetails['paymentFrequency'],
                    interestRate: Number(step.interestRate) || prev.interestRate,
                }));
            } else if (t === 'debtRepayment' && typeof step.liabilityKey === 'string') {
                // The engine keys a debt "<category>:<id>"; the payoff card keys it "<category>::<id>".
                const at = step.liabilityKey.indexOf(':');
                if (at > 0) nextDebt[`${step.liabilityKey.slice(0, at)}::${step.liabilityKey.slice(at + 1)}`] = Number(step.repaymentAmount) || 0;
                // ...paid from the same accounts the plan sells (25 Oct 2026), not whatever the bank holds.
                for (const a of fundingAsAllocations(step)) nextDebtFunding[`${a.assetType}-${a.assetId}`] = (nextDebtFunding[`${a.assetType}-${a.assetId}`] || 0) + a.amount;
            }
        }
        setManualStrategies(nextManual);
        setPropertyStrategy(nextProperty);
        setOverallStrategy((prev) => ({ ...prev, reverseMortgage: nextProperty === 'reverseMortgage', heas: nextProperty === 'heas' }));
        setSelectedFundingStrategy(nextAnnuity ? 'annuity' : null);
        setAnnuityRecord(nextAnnuity);
        setDebtPayments(nextDebt);
        setDebtFunding(nextDebtFunding);
        setDebtRepaymentBasis('keep_repayment');
        setManualSeededFrom(row ? sahPlanWords(config as never, factFind as never, String(row.name ?? '')).title : null);
        handleStrategyModeChange('manual');
        scrollAppToTop();
    };

    /** What the plan page asked for when it opened this step (read once; sahChosenPlan.ts). */
    const [hubIntent] = useState(() => takeSahHubIntent());
    useEffect(() => {
        if (hubIntent?.kind === 'build') buildFromEnginePlan({ config: hubIntent.config, name: hubIntent.name });
        else if (hubIntent?.kind === 'build-new') buildFromEnginePlan(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Check if any strategies are actually applied
    const hasStrategies = useMemo(() => {
        return Number(manualStrategies.gifting) > 0 ||
               Number(manualStrategies.funeralBonds) > 0 ||
               propertyStrategy !== 'none' ||
               overallStrategy.reverseMortgage ||
               overallStrategy.heas ||
               Boolean(selectedAgedCareIntegration) ||
               selectedFundingStrategy === 'annuity';
    }, [manualStrategies, propertyStrategy, overallStrategy, selectedAgedCareIntegration, selectedFundingStrategy]);
    
    const [afterContributions, setAfterContributions] = useState<SahContributionsResult | null>(null);
    useEffect(() => {
        // If no strategies are applied, use beforeContributions (which matches ContributionsSection exactly)
        if (!hasStrategies && beforeContributions) {
            setAfterContributions(beforeContributions);
            return undefined;
        }

        if (!afterFinancials || !optimizedScenario?.optimizedFactFind) { setAfterContributions(null); return undefined; }

        const hasAcatLevel = factFind?.careNeeds?.clientAcat?.status === 'yes' && factFind?.careNeeds?.clientAcat?.homeCareLevel;
        const hasPartnerAcatLevel = factFind?.adviceFor === 'couple' && factFind?.careNeeds?.partnerAcat?.status === 'yes' && factFind?.careNeeds?.partnerAcat?.homeCareLevel;

        if (!hasAcatLevel && !hasPartnerAcatLevel) {
            setAfterContributions(null);
            return undefined;
        }

        // Strategies are applied - use optimizedFactFind (which has strategy changes)
        const baseFactFind = structuredClone(optimizedScenario.optimizedFactFind);
        // The SAME resolved plan as the before call and the Suggestion Engine.
        baseFactFind.servicePlan = planForPricing as never;

        let cancelled = false;
        trackSah(calculationApi.calculateIndividualContributions(
            baseFactFind,
            afterFinancials,
            participantType,
            nwopCategory,
            meansDisclosed,
            Number(previousITCF) || 0,
            0.5,
            { strategyOptions: agedCareResidencyOverrides }
        )).then((r) => { if (!cancelled) setAfterContributions(r); });
        return () => { cancelled = true; };
    }, [hasStrategies, beforeContributions, afterFinancials, optimizedScenario, factFind, participantType, nwopCategory, meansDisclosed, previousITCF, agedCareResidencyOverrides, resolvedServicePlan]);
    
    // Get contribution data for display (needed for projections)
    const afterContributionData = isPartnered 
        ? (afterContributions?.combined || afterContributions?.client)
        : afterContributions?.client;

    /*
     * A service plan priced the way the engine prices it (server calculateServicePlanCosts): the plan's
     * services at the assessed rates within each person's budget, plus anything above the budget at
     * full price (decision 3, 19 Oct 2026). The assessment's own `totalAnnual` charged the rates on the
     * whole plan, so an over-budget plan was charged less here than the engine charged the same plan.
     */
    const servicePlanBudgets = useMemo(() => servicePlanBudgetsOf(factFind as never), [factFind]);
    const priceOnPlan = (rates: SahContributionRateSet | undefined | null): ServicePlanCosts | null =>
        rates ? calculateServicePlanCosts(resolvedServicePlan.plan as never, rates as never, isPartnered, servicePlanBudgets) : null;

    // Calculate projections for legacy estimate
    // Calculate combined care costs separately so it's available for the modal
    const combinedCareCosts = useMemo<CombinedCareCosts | null>(() => {
        if (!afterContributionData) return null;

        // Check if services are actually selected (not just using example distribution)
        // A resolved plan (real or illustrative) is always present now — see resolvedServicePlan.
        const hasServicePlan = true;

        const hasBudget = (afterContributionData?.budget?.total ?? 0) > 0 && hasServicePlan;
        const annualContribution = hasBudget ? (priceOnPlan(afterContributionData.contributionRates)?.contribution.total ?? 0) : 0;
        const agedCareAnnualCost = selectedAgedCareIntegration?.annualCost || 0;

        const supportAtHomeDaily = annualContribution / 365;
        const agedCareDaily = agedCareAnnualCost / 365;

        return {
            totalDailyCost: supportAtHomeDaily + agedCareDaily,
            supportAtHomeDailyCost: supportAtHomeDaily,
            agedCareDailyCost: agedCareDaily,
            supportAtHomeAnnualCost: annualContribution,
            agedCareAnnualCost
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [afterContributionData, selectedAgedCareIntegration, factFind, resolvedServicePlan, servicePlanBudgets, isPartnered]);

    /** Care costs for pre-strategy FY blend: profile financials + before optimisation contributions (no linked RAC annual from strategy integration). */
    const combinedCareCostsBaseline = useMemo<CombinedCareCosts | null>(() => {
        if (!beforeContributionData) return null;

        // A resolved plan (real or illustrative) is always present now — see resolvedServicePlan.
        const hasServicePlan = true;

        const hasBudget = (beforeContributionData?.budget?.total ?? 0) > 0 && hasServicePlan;
        const annualContribution = hasBudget ? (priceOnPlan(beforeContributionData.contributionRates)?.contribution.total ?? 0) : 0;
        const agedCareAnnualCost = 0;

        const supportAtHomeDaily = annualContribution / 365;
        const agedCareDaily = agedCareAnnualCost / 365;

        return {
            totalDailyCost: supportAtHomeDaily + agedCareDaily,
            supportAtHomeDailyCost: supportAtHomeDaily,
            agedCareDailyCost: agedCareDaily,
            supportAtHomeAnnualCost: annualContribution,
            agedCareAnnualCost,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [beforeContributionData, factFind, resolvedServicePlan, servicePlanBudgets, isPartnered]);

    /**
     * The shared debt strategy — see the Aged Care module. Handed to the ENGINE via the projection
     * options; the engine performs the payoff at the projection's start (a lender takes the money any
     * day), the loan-repayment plugin stops servicing whatever is cleared, and the projections and
     * their charts reflect it without this page doing any arithmetic of its own.
     */
    /**
     * When Support at Home services BEGIN (`YYYY-MM-DD`), blank = already receiving them.
     *
     * A fact about the case, not about any one strategy — so it is passed to the manual projection
     * AND down into the Suggestion Engine, where the server merges it over the baseline and every
     * candidate alike. Comparing strategies that assumed different start dates would compare nothing.
     *
     * The engine treats it exactly as the aged-care entry date treats care fees: nothing is charged
     * before the day. The stay-at-home side effects of that flag were audited before this was wired —
     * living expenses do not switch (no in-care figure is set) and the home-letting clock never starts
     * (rentHome is never set here) — so the date means "support costs start here" and nothing else.
     */
    const supportStartInPast = supportStartDate !== '' && supportStartDate < new Date().toISOString().slice(0, 10);

    // Restored with the plan, so reopening a plan built by hand gives back the same plan (its fingerprint).
    const [debtPayments, setDebtPayments] = useState<Record<string, number>>(() => ((restoredStrategy?.inputs as { debtPayments?: Record<string, number> } | undefined)?.debtPayments) || {});
    // Where the payoff money comes from, and the repayment choice, are restored too (25 Oct 2026): without
    // them a reopened plan fell back to "cash first" and named a different account from the one chosen.
    const [debtFunding, setDebtFunding] = useState<Record<string, number>>(() => ((restoredStrategy?.inputs as { debtFunding?: Record<string, number> } | undefined)?.debtFunding) || {});
    const [debtRepaymentBasis, setDebtRepaymentBasis] = useState<'keep_repayment' | 'lower_repayment'>(() => ((restoredStrategy?.inputs as { debtRepaymentBasis?: string } | undefined)?.debtRepaymentBasis === 'lower_repayment' ? 'lower_repayment' : 'keep_repayment'));
    const debtPayoffPlan = useMemo<DebtPayoff | null>(() => {
        const payments = Object.fromEntries(Object.entries(debtPayments).filter(([, amount]) => (Number(amount) || 0) > 0));
        if (Object.keys(payments).length === 0) return null;
        const funding = Object.fromEntries(Object.entries(debtFunding).filter(([, amount]) => (Number(amount) || 0) > 0));
        return { payments, funding, repaymentBasis: debtRepaymentBasis };
    }, [debtPayments, debtFunding, debtRepaymentBasis]);

    const sahProjectionStrategyOptions = useMemo<Record<string, unknown>>(() => {
        const strategyOptions: Record<string, unknown> = {
            reverseMortgage: overallStrategy.reverseMortgage,
            reverseMortgageDetails: overallStrategy.reverseMortgage ? reverseMortgageDetails : null,
            heas: overallStrategy.heas,
            heasDetails: overallStrategy.heas ? heasDetails : null,
            inflationAssumptionPct: 2.5,
            projectionPeriodMode: 'financial_year',
            ...agedCareResidencyOverrides,
        };

        if (selectedAgedCareIntegration) {
            strategyOptions.integratedAgedCareDetails = selectedAgedCareIntegration;
            if (selectedAgedCareIntegration.livingExpenses !== undefined && selectedAgedCareIntegration.livingExpenses !== null) {
                strategyOptions.scenarioLivingExpenses = selectedAgedCareIntegration.livingExpenses;
            }
        }

        if (selectedFundingStrategy === 'annuity' && annuityRecordForCalculation) {
            strategyOptions.annuities = [annuityRecordForCalculation];
        }

        if (debtPayoffPlan) strategyOptions.debtPayoff = debtPayoffPlan;

        // A past date means support is already under way, which is what a BLANK date means — sending
        // it would defer costs to a day already gone.
        if (supportStartDate && !supportStartInPast) strategyOptions.careStartDate = supportStartDate;

        return strategyOptions;
    }, [
        debtPayoffPlan,
        supportStartDate,
        supportStartInPast,
        overallStrategy.reverseMortgage,
        overallStrategy.heas,
        reverseMortgageDetails,
        heasDetails,
        agedCareResidencyOverrides,
        selectedAgedCareIntegration,
        selectedFundingStrategy,
        annuityRecordForCalculation,
    ]);

    /*
     * A PARTNER'S AGED CARE FEES ARE CHARGED AS THE ENGINE CHARGES THEM (25 Oct 2026, pack B.SAH.6). The
     * engine charges the Support at home contribution plus the linked plan's yearly fees as one integrated
     * cost, held flat in real terms. This screen handed the same fees over as a bare daily total, which the
     * projection treats as a residential schedule: once the cash ran low it drew the fees from the room
     * deposit and charged the accommodation payment on what was drawn. So a hand-built plan that changed
     * nothing ended $78,894 below the engine's "doing nothing" on the same household. Both now run the
     * engine's way; with no aged care plan linked nothing changes.
     */
    const linkedFeesAYear = selectedAgedCareIntegration ? Number(selectedAgedCareIntegration.annualCost) || 0 : 0;
    const planProjection = useMemo(
        () => (combinedCareCosts
            ? sahProjectionArgs({ options: sahProjectionStrategyOptions, costs: combinedCareCosts, supportAtHomeAYear: combinedCareCosts.supportAtHomeAnnualCost, linkedFeesAYear })
            : null),
        [sahProjectionStrategyOptions, combinedCareCosts, linkedFeesAYear],
    );
    const planProjectionOptions = planProjection?.options ?? sahProjectionStrategyOptions;
    const planProjectionCosts = planProjection?.costs ?? null;

    const [projections, setProjections] = useState<SahProjectionYear[] | null>(null);
    useEffect(() => {
        if (!afterFinancials || !afterContributionData || !optimizedScenario?.optimizedFactFind || !planProjectionCosts) { setProjections(null); return undefined; }

        let cancelled = false;
        trackSah(calculationApi.runProjections(optimizedScenario.optimizedFactFind, planProjectionOptions, planProjectionCosts, legacyYears, { debug: false })).then((proj) => {
            if (!cancelled) setProjections(proj);
        });
        return () => { cancelled = true; };
    }, [optimizedScenario, afterFinancials, afterContributionData, legacyYears, planProjectionOptions, planProjectionCosts]);

    /*
     * THE FINANCIAL-YEAR / CALENDAR-YEAR TOGGLE (owner, 23 Sep 2026: present in Aged Care, missing
     * everywhere else). Not a display filter — the engine PROJECTS the year it REPORTS, so the rows
     * are cut again by a second run, which is exactly what the shared modal gates the toggle on.
     * Fetched only while the modal is open, so a remembered 'calendar_year' does not fire a second
     * run on every page load. The mode is the modal's own sticky preference, shared with Aged Care.
     */
    const [modalPeriodMode, setModalPeriodMode] = useState<ProjectionsModalPeriodMode>(readPeriodMode);
    const [calendarYearProjections, setCalendarYearProjections] = useState<SahProjectionYear[] | null>(null);
    useEffect(() => {
        if (modalPeriodMode !== 'calendar_year') { setCalendarYearProjections(null); return undefined; }
        if (!showProjectionsModal) return undefined;
        if (!optimizedScenario?.optimizedFactFind || !planProjectionCosts) return undefined;
        let cancelled = false;
        setCalendarYearProjections(null);
        calculationApi.runProjections(
            optimizedScenario.optimizedFactFind,
            { ...planProjectionOptions, projectionPeriodMode: 'calendar_year' },
            planProjectionCosts,
            legacyYears,
            { debug: false },
        ).then((proj) => { if (!cancelled) setCalendarYearProjections(proj); });
        return () => { cancelled = true; };
    }, [showProjectionsModal, modalPeriodMode, optimizedScenario, planProjectionOptions, planProjectionCosts, legacyYears]);

    /** Adds FY full-year pre/post blend on row 1 (same pattern as Aged Care ProjectionsModal). */
    /*
     * The benchmark: this household RECEIVING THE SAME SERVICES with no financial strategy, projected
     * over the same period. Support at Home is a care requirement, so "do nothing" still pays for
     * care — which is why the baseline carries combinedCareCostsBaseline rather than zero costs.
     */
    /*
     * THE SAME "DOING NOTHING" AS THE ENGINE'S (25 Oct 2026). It was run on the profile as entered — both
     * at home, before the aged care plan — while every plan here, and the engine's benchmark, start from
     * the household once that plan is carried out. It now runs on that household, with the engine's
     * options (supportAtHome.ts computeSupportAtHomeBaselineData): the living costs, the residency, the
     * support start date, and the contribution plus the aged care fees as one cost. A plan that changes
     * nothing therefore shows no change on any figure.
     */
    // The engine's baseline household: the aged care plan carried out, or — where it cannot be rebuilt — its
    // deposit paid, as the plans here pay it (sahBaselineFactFind).
    const doNothingFactFind = useMemo<DraftFactFind>(
        () => (selectedAgedCareIntegration ? sahBaselineFactFind(factFind, selectedAgedCareIntegration, undefined, linkedPosition.positioned) : factFind),
        [factFind, selectedAgedCareIntegration, linkedPosition.positioned],
    );
    const doNothingProjection = useMemo(() => {
        if (!combinedCareCostsBaseline) return null;
        const timing = supportStartDate && !supportStartInPast ? { careStartDate: supportStartDate } : {};
        const base = linkedFeesAYear > 0
            ? { scenarioLivingExpenses: Number(doNothingFactFind?.expenses?.livingExpenses) || 0, ...agedCareResidencyOverrides, ...timing }
            : timing;
        return sahProjectionArgs({ options: base, costs: combinedCareCostsBaseline, supportAtHomeAYear: combinedCareCostsBaseline.supportAtHomeAnnualCost, linkedFeesAYear });
    }, [supportStartDate, supportStartInPast, linkedFeesAYear, doNothingFactFind, agedCareResidencyOverrides, combinedCareCostsBaseline]);
    const sahDoNothingRun = useDoNothingProjection(doNothingFactFind, legacyYears, {
        enabled: !!doNothingProjection,
        strategyOptions: doNothingProjection?.options,
        agedCareCosts: (doNothingProjection?.costs ?? undefined) as unknown as Record<string, unknown>,
        track: trackSah,
    });
    const sahDoNothingLegacy = sahDoNothingRun.legacy;
    /** Year one of each projection — the engine's "each year" figure (getProjectionCashFlow on row 0). */
    const sahDoNothingYear1 = sahDoNothingRun.rows?.[0] ?? null;

    const [projectionsForModal, setProjectionsForModal] = useState<SahProjectionYear[] | null>(null);

    /**
     * How long the money lasts with NO strategy — the starting position's runway.
     *
     * Reads the same projection rows the panel does, through the same shared helper Aged Care uses,
     * so the anchor note and the panel cannot report different years. Null when there is nothing to
     * project yet, in which case the beat is simply omitted.
     */
    const startingRunway = useMemo(
        () => planRunwayCopy(projections as never, Number(legacyYears) || 5),
        [projections, legacyYears],
    );
    useEffect(() => {
        if (!projections?.length || !combinedCareCosts || !optimizedScenario?.optimizedFactFind) { setProjectionsForModal(projections); return undefined; }

        const mode = sahProjectionStrategyOptions.projectionPeriodMode ?? 'financial_year';
        if (mode === 'strategy_year') { setProjectionsForModal(projections); return undefined; }

        const baselineCosts = combinedCareCostsBaseline ?? {
            totalDailyCost: 0,
            supportAtHomeDailyCost: 0,
            agedCareDailyCost: 0,
            supportAtHomeAnnualCost: 0,
            agedCareAnnualCost: 0,
        };

        let cancelled = false;
        calculationApi.computeYearOneFullAustralianFyBlendSupportAtHome({
            strategyOptions: sahProjectionStrategyOptions,
            // The household the plans start from (the aged care plan carried out), as the benchmark is.
            baselineFactFind: householdFactFind,
            strategyFactFind: optimizedScenario.optimizedFactFind,
            baselineCombinedCareCosts: baselineCosts,
            strategyCombinedCareCosts: combinedCareCosts,
            strategyYearOneProjection: projections[0],
            initialFinancials: beforeFinancials,
            currentFinancials: afterFinancials,
            legacyYears,
            options: { debug: false },
        }).then((fyView) => {
            if (cancelled) return;
            setProjectionsForModal(projections.map((row, i) => (i === 0 ? { ...row, fyFullYearView: fyView } : row)));
        });
        return () => { cancelled = true; };
    }, [
        projections,
        combinedCareCosts,
        combinedCareCostsBaseline,
        optimizedScenario,
        factFind,
        householdFactFind,
        beforeFinancials,
        afterFinancials,
        legacyYears,
        sahProjectionStrategyOptions,
    ]);
    
    // Calculate projected legacy from projections
    const projectedLegacy = useMemo<number>(() => {
        if (!projections || projections.length === 0) return afterFinancials?.netWorth || 0;
        const lastYear = projections[projections.length - 1];
        return lastYear.netWorth || 0;
    }, [projections, afterFinancials]);

    const formatCurrency = (value: number | string | null | undefined) => {
        return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(value) || 0);
    };

    const formatPercent = (value: number | undefined) => {
        return `${((value ?? 0) * 100).toFixed(1)}%`;
    };

    // CalculationTooltip is now defined at module level, accessible to both modal and StrategiesSection

    /*
     * Support at Home used its OWN copy of ImpactMetric — a different design that showed only the
     * AFTER figure with a delta beside it, never the baseline it was measured against. It is now the
     * shared component (owner-guided, 2026-08-26: build it correctly and make it scalable).
     *
     * Two things follow. The module stops looking like a different product from the other four, and
     * every improvement to the shared tile — the before-arrow-after grammar, the row alignment, the
     * named benchmark — reaches it without being rebuilt here. A private copy of a shared component
     * is how one module quietly falls behind and how the same fix comes to cost twice.
     */

    const serviceCosts = null; // Will be calculated if needed
    
    // Prepare contribution rates with before/after data for Insight Panel
    const contributionRatesForInsight = {
        before: beforeContributionData?.contributionRates || {},
        after: afterContributionData?.contributionRates || {}
    };

    const isSuggestionMode = internalStrategyMode === 'suggestion';

    /**
     * What the plan summary is describing, in the client's words — or null when it is describing
     * nothing yet, in which case the panel says so and holds the starting position.
     *
     * Derived from the strategies actually switched ON, never a stored label, for the same reason
     * Aged Care does it that way: a panel headed with a name that no longer matches the figures
     * underneath it is worse than one with no name at all.
     */
    const sahPlanSummaryName = useMemo<string | null>(() => {
        if (isSuggestionMode) {
            return (sahSeSelectedStrategy as { name?: string } | null)?.name ?? null;
        }
        const parts: string[] = [];
        /*
         * EVERY LEVER `hasStrategies` counts is named here (owner-caught, 2026-09-24: a $10,000 gift
         * moved every figure in the panel while its heading still read "No plan yet — the starting
         * position"). A heading that says nothing is happening over figures that have moved is the
         * one thing this panel must never do.
         */
        if (Number(manualStrategies.gifting) > 0) parts.push(`Gift ${formatCurrency(Number(manualStrategies.gifting))}`);
        if (Number(manualStrategies.funeralBonds) > 0) parts.push(`Buy a funeral bond for ${formatCurrency(Number(manualStrategies.funeralBonds))}`);
        if (propertyStrategy === 'downsize') parts.push('Downsize the home');
        if (propertyStrategy === 'renovate') parts.push('Renovate the home');
        if (propertyStrategy === 'upgrade') parts.push('Upgrade the home');
        if (propertyStrategy === 'reverseMortgage') parts.push('Take a reverse mortgage');
        if (propertyStrategy === 'heas') parts.push('Use the Home Equity Access Scheme');
        if (annuityRecord) parts.push('Commence an annuity');
        if (selectedAgedCareStrategyId) parts.push('Link an aged care plan');
        if (!parts.length) return null;
        const [lead, ...rest] = parts;
        return rest.length
            ? `${lead}, ${rest.map((p) => p.charAt(0).toLowerCase() + p.slice(1)).join(', ')}`
            : lead;
    }, [isSuggestionMode, sahSeSelectedStrategy, manualStrategies, propertyStrategy, annuityRecord, selectedAgedCareStrategyId]);

    const panelContributionData = isSuggestionMode ? beforeContributionData : afterContributionData;
    const panelFinancials = isSuggestionMode ? beforeFinancials : afterFinancials;
    const impactAfterFinancials = isSuggestionMode ? beforeFinancials : afterFinancials;
    /*
     * The estate the summary shows in engine mode (review, 18 Oct 2026: picking "Suggestion Engine"
     * dropped it from $4,125,171 to $3,445,000, "−$680,171 vs no plan", with no plan chosen — the
     * $3,445,000 was today's net worth, not a projected estate). With a plan selected it is that
     * plan's projected estate; with none, it is the do-nothing estate, so the panel moves by nothing.
     */
    const selectedSeLegacy = Number((sahSeSelectedStrategy as { snapshot?: { legacyValue?: number } } | null)?.snapshot?.legacyValue);
    const impactProjectedLegacy = isSuggestionMode
        ? (Number.isFinite(selectedSeLegacy) && selectedSeLegacy > 0 ? selectedSeLegacy : (sahDoNothingLegacy ?? projectedLegacy))
        : projectedLegacy;
    const showContributionWas = !isSuggestionMode;

    /*
     * CASHFLOW NET OF THE SERVICE PLAN (owner-caught, 2026-09-24). The annual position's cashflow
     * is "money in, less money out" BEFORE any care costs — the standalone assessment never sees
     * the Support at Home contribution, which the projection charges as a monthly stream instead.
     * So with "Service Plan A" selected the panel still read $10,198 while the Service Builder, one
     * tab back, read $4,214 remaining after the $5,984 contribution: same client, same plan, two
     * cashflows. The panel now nets the plan's contribution the way the Service Builder does — the
     * share of care costs shown in the tile directly above it — before AND after, so the movement
     * between them is the strategy's alone, and year one of the projection and the annual position
     * finally agree. No service plan, no dollars: on the illustrative package the contribution is
     * not quoted (see sahInBrief), so the cashflow stays gross and the starting-position note says
     * "before any care costs" and stops there.
     */
    const servicePlanName = useMemo<string | null>(() => {
        const o = servicePlanResolution.origin;
        return o.kind === 'saved' ? o.name : o.kind === 'current' ? (o.basedOnName ?? null) : o.kind === 'restore' ? o.name : null;
    }, [servicePlanResolution]);
    /*
     * THEIR CONTRIBUTION ON THIS SERVICE PLAN, priced by the engine's own arithmetic (19 Oct 2026):
     * the plan's services at the assessed rates, within each person's budget, plus anything above the
     * budget at full price (decision 3, pack B.SAH.3). The assessment's `totalAnnual` charged the rates
     * on the whole plan, so an over-budget plan read cheaper here than on the engine's card for the
     * same plan.
     */
    const sahPlanCostsBefore = !resolvedServicePlan.isEstimated ? priceOnPlan(beforeContributionData?.contributionRates) : null;
    const sahPlanCostsAfter = !resolvedServicePlan.isEstimated ? priceOnPlan(panelContributionData?.contributionRates) : null;
    const sahContributionBefore = sahPlanCostsBefore ? sahPlanCostsBefore.contribution.total : null;
    const sahContributionAfter = sahPlanCostsAfter ? sahPlanCostsAfter.contribution.total : null;
    /*
     * Year one of the projection where there is one, as the engine quotes it (getProjectionCashFlow on
     * row 0; 19 Oct 2026: the yearly snapshot less the contribution read $715 short where the engine's
     * strip read $633 for the same doing-nothing). The snapshot figure stands in while the run loads.
     */
    const year1CashFlow = (row: { cashFlow?: unknown } | null | undefined): number | null =>
        row && Number.isFinite(Number(row.cashFlow)) && row.cashFlow != null ? Number(row.cashFlow) : null;
    const sahCashFlowBefore = beforeFinancials?.cashFlow == null ? null
        : (!resolvedServicePlan.isEstimated ? year1CashFlow(sahDoNothingYear1) : null) ?? beforeFinancials.cashFlow - (sahContributionBefore ?? 0);
    const sahCashFlowAfter = impactAfterFinancials?.cashFlow == null ? null
        : isSuggestionMode ? sahCashFlowBefore
            : (!resolvedServicePlan.isEstimated && hasStrategies ? year1CashFlow(projections?.[0]) : !resolvedServicePlan.isEstimated ? year1CashFlow(sahDoNothingYear1) : null)
                ?? impactAfterFinancials.cashFlow - (sahContributionAfter ?? 0);
    const sahCashFlowBreakdown = useMemo<BreakdownRow[] | null>(() => {
        const rows = impactAfterFinancials?.cashFlowBreakdown;
        if (!rows || sahContributionAfter == null) return null;
        return [
            ...rows,
            { label: '--- Support at Home ---', value: '' },
            { label: `Less: Your share of care costs${servicePlanName ? ` (${servicePlanName})` : ''}`, value: `-${formatCurrency(sahContributionAfter)}` },
        ];
    }, [impactAfterFinancials, sahContributionAfter, servicePlanName]);

    /**
     * The panel's "In brief" — one sentence saying what the plan does, in the same grammar Aged
     * Care uses ("what it costs, then each figure that moved and by how much").
     *
     * Built ONLY from figures already rendered in the tiles directly beneath it, subtracted exactly
     * as the tiles subtract them, so the sentence cannot disagree with the numbers it summarises.
     * Nothing here re-reads the fact-find or calls an engine.
     *
     * The Support at Home difference from Aged Care is what leads: this module charges a SHARE of
     * each service rather than a room, so the client's own contribution is the cost that moves, and
     * it leads the sentence where the DAP leads Aged Care's.
     */
    /*
     * ─── THE PLAN BUILT BY HAND, IN THE ENGINE'S TERMS (owner, 19 Oct 2026: Manual Control keeps the
     * engine's look and feel — the same card, Explain this, What could go wrong, buttons and words). ───
     *
     * The levers are described as the engine's pieces so the same words name them (sahPlanWords), and
     * the figures are gathered into the same shape the engine card reads (SahFigures). Every figure is
     * one already on this screen: the rail and the card cannot disagree.
     */
    /** The loan against the home in a hand-built plan, off the same projection the card shows (closing balance from its last row). */
    const manualEquityRelease = (() => {
        const rows = (projections || []) as Array<Record<string, unknown>>;
        const last = rows[rows.length - 1] || {};
        if (propertyStrategy === 'reverseMortgage' && Number(reverseMortgageDetails.loanAmount) > 0) {
            return { kind: 'reverseMortgage' as const, loanAmount: Number(reverseMortgageDetails.loanAmount) || 0, interestRate: Number(reverseMortgageDetails.interestRate) || 0, projectedBalance: Number(last.reverseMortgageLoanBalance) || 0, years: rows.length };
        }
        if (propertyStrategy === 'heas' && Number(heasDetails.paymentAmount) > 0) {
            const perYear = heasDetails.paymentFrequency === 'fortnightly' ? Number(heasDetails.paymentAmount) * 26 : Number(heasDetails.paymentAmount) * 12;
            return { kind: 'heas' as const, loanAmount: Math.round(perYear * rows.length), interestRate: Number(heasDetails.interestRate) || 0, projectedBalance: Number(last.reverseMortgageLoanBalance) || 0, years: rows.length };
        }
        return null;
    })();
    const manualPieces = useMemo<SahConfigLike[]>(() => {
        const pieces: SahConfigLike[] = [];
        if (Number(manualStrategies.gifting) > 0) pieces.push({ type: 'gifting', amount: Number(manualStrategies.gifting) });
        if (Number(manualStrategies.funeralBonds) > 0) pieces.push({ type: 'funeralBonds', amount: Number(manualStrategies.funeralBonds) });
        if (selectedFundingStrategy === 'annuity' && annuityRecord && Number(annuityRecord.purchaseAmount) > 0) {
            const residual = Number(annuityRecord.residualCapital) || 0;
            pieces.push({
                type: 'annuity',
                purchaseAmount: Number(annuityRecord.purchaseAmount),
                residualCapital: residual > 0 ? 1 : 0,
                annuityType: String(annuityRecord.annuityType || 'lifetime'),
                ownership: annuityRecord.owner === 'partner' ? 'partner' : 'client',
                name: residual > 0 ? 'annuity (capital returned)' : 'annuity (nil residual)',
            });
        }
        // The accounts chosen in the allocator, in the engine's terms, so the card says "Paid from …" too.
        const funded = (sources: Array<{ assetId?: unknown; assetType?: unknown; amount?: unknown }> | undefined) =>
            (sources || []).filter((s) => Number(s.amount) > 0).map((s) => ({ type: String(s.assetType), id: s.assetId as string | number, amountToTake: Number(s.amount) }));
        if (propertyStrategy === 'renovate' && Number(renovateDetails.renovationAmount) > 0) pieces.push({ type: 'renovate', renovationAmount: Number(renovateDetails.renovationAmount), fundingSources: funded(renovateDetails.fundingSources) });
        if (propertyStrategy === 'upgrade' && Number(upgradeDetails.newHomePurchasePrice) > 0) pieces.push({ type: 'upgrade', newHomeValue: Number(upgradeDetails.newHomePurchasePrice), fundingSources: funded(upgradeDetails.fundingSources) });
        if (propertyStrategy === 'downsize' && Number(downsizeDetails.newHomePurchasePrice) >= 0 && Number(downsizeDetails.salePrice) > 0) {
            const usesDownsizer = Number(downsizeDetails.downsizerContribution) > 0 || Number(downsizeDetails.partnerDownsizerContribution) > 0;
            pieces.push({ type: 'downsize', newHomeValue: Number(downsizeDetails.newHomePurchasePrice) || 0, downsizeDetails: { useDownsizer: usesDownsizer }, name: usesDownsizer ? 'downsizer' : '' });
        }
        if (propertyStrategy === 'reverseMortgage' && Number(reverseMortgageDetails.loanAmount) > 0) pieces.push({ type: 'reverseMortgage', loanAmount: Number(reverseMortgageDetails.loanAmount) });
        if (propertyStrategy === 'heas' && Number(heasDetails.paymentAmount) > 0) pieces.push({ type: 'heas', paymentAmount: Number(heasDetails.paymentAmount), paymentFrequency: heasDetails.paymentFrequency });
        for (const [key, amount] of Object.entries(debtPayments)) {
            if (!(Number(amount) > 0)) continue;
            const at = key.indexOf('::');
            const list = ((factFind?.liabilities as Record<string, Array<{ id?: unknown; description?: string; balance?: unknown }>> | undefined)?.[key.slice(0, at)] || []);
            const liability = list.find((l) => String(l?.id) === key.slice(at + 2));
            const balance = Number(liability?.balance) || 0;
            pieces.push({ type: 'debtRepayment', repaymentAmount: Number(amount), liabilityDescription: String(liability?.description || 'debt'), name: balance > 0 && Number(amount) < balance ? 'partial' : '' });
        }
        return pieces;
    }, [manualStrategies, selectedFundingStrategy, annuityRecord, propertyStrategy, renovateDetails, upgradeDetails, downsizeDetails, reverseMortgageDetails, heasDetails, debtPayments, factFind]);

    const manualWords = useMemo(() => {
        const config: SahConfigLike | null = manualPieces.length > 1 ? { type: 'combination', steps: manualPieces } : manualPieces[0] ?? null;
        const words = sahPlanWords(config, factFind as never);
        const linked = selectedAgedCareIntegration
            ? `Priced with the aged care plan "${String((selectedAgedCareStrategy as { displayName?: string } | null)?.displayName || selectedAgedCareStrategy?.name || 'chosen')}" for the one moving into care.`
            : '';
        if (!config && linked) return { title: 'Keep things as they are, with the aged care plan', sub: linked, treatment: [] as string[] };
        // A gift or funeral bond is taken from a KIND of account here (not a named one, as the engine does).
        const kindWords: Record<string, string> = { bank: 'the bank accounts', abp: 'the account-based pensions', shares: 'the shares' };
        const fromKind = !words.sub
            ? [...new Set([
                Number(manualStrategies.gifting) > 0 ? kindWords[String(manualStrategies.giftingSource)] : null,
                Number(manualStrategies.funeralBonds) > 0 ? kindWords[String(manualStrategies.funeralSource)] : null,
            ].filter(Boolean))]
            : [];
        const kindSentence = fromKind.length ? `Paid from ${fromKind.join(' and ')}.` : '';
        return { ...words, sub: [words.sub || kindSentence, linked].filter(Boolean).join(' ') };
    }, [manualPieces, factFind, selectedAgedCareIntegration, selectedAgedCareStrategy, manualStrategies]);

    /** One plan's figures in the shape the engine card and its dialogs read. */
    const sahFiguresOf = (fin: SahFinancialsSnapshot | null, costs: ServicePlanCosts | null, rates: SahContributionRateSet | undefined, legacy: number | null, year1: DoNothingRow | SahProjectionYear | null): SahFigures | null => {
        if (!fin || !costs || legacy == null) return null;
        const contribution = costs.contribution.total;
        // The engine's snapshot: each year and investment income from year one of the projection,
        // the pension and tax from the yearly assessment (supportAtHome.ts, evaluate…Strategy).
        const y1Cash = year1CashFlow(year1);
        return {
            contribution,
            pension: Number(fin.socialSecurity) || 0,
            cashflow: y1Cash ?? (Number(fin.cashFlow) || 0) - contribution,
            legacy,
            investmentIncome: Number(year1?.investmentIncome) || Number(fin.investmentIncome) || 0,
            rates: rates ? { independence: Number(rates.independence) || 0, everydayLiving: Number(rates.everydayLiving) || 0 } : null,
            breakdown: {
                clinical: costs.clinical, independence: costs.independence, everydayLiving: costs.everyday,
                independenceContribution: costs.contribution.independence, everydayContribution: costs.contribution.everyday, privateExcess: costs.privateExcess,
            },
            livingExpenses: Number((factFind?.expenses as { livingExpenses?: unknown } | undefined)?.livingExpenses) || 0,
            taxPayable: Number(fin.netTax) || 0,
            pensionRows: (fin.socialSecurityBreakdown as Array<{ label?: string; value?: unknown }>) || [],
            cashFlowBreakdown: (year1?.cashFlowBreakdown as unknown) || fin.cashFlowBreakdown,
        };
    };
    const manualBaseFigures = useMemo(
        () => sahFiguresOf(beforeFinancials, priceOnPlan(beforeContributionData?.contributionRates), beforeContributionData?.contributionRates, sahDoNothingLegacy ?? null, sahDoNothingYear1),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [beforeFinancials, beforeContributionData, sahDoNothingLegacy, sahDoNothingYear1, resolvedServicePlan, servicePlanBudgets, factFind],
    );
    const manualFiguresReady = !hasStrategies || (afterFinancials != null && afterContributionData != null && projections != null);
    const manualPlanFigures = useMemo(
        () => (manualFiguresReady ? sahFiguresOf(afterFinancials, priceOnPlan(afterContributionData?.contributionRates), afterContributionData?.contributionRates, projectedLegacy, projections?.[0] ?? null) : null),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [manualFiguresReady, afterFinancials, afterContributionData, projectedLegacy, projections, resolvedServicePlan, servicePlanBudgets, factFind],
    );
    /** The levers as a fingerprint, so choosing the same plan twice replaces it rather than adding one. */
    const manualFingerprint = useMemo(() => JSON.stringify({ manualPieces, linked: selectedAgedCareStrategyId || null, plan: currentServicePlanBasis }), [manualPieces, selectedAgedCareStrategyId, currentServicePlanBasis]);
    /** The chosen record on file IS the plan on screen. */
    const isChosenManualPlan = (s: SavedStrategy): boolean => {
        if (!(s as { chosen?: boolean }).chosen) return false;
        const inp = (s.inputs || {}) as { manualFingerprint?: string; card?: { words?: { title?: string }; plan?: SahFigures } };
        if (inp.manualFingerprint === manualFingerprint) return true;
        // "Customise this plan", unchanged: the same plan as the tested one chosen — same words, same figures.
        const c = inp.card;
        return !!(c?.plan && manualPlanFigures && c.words?.title === manualWords.title
            && Math.abs(c.plan.legacy - manualPlanFigures.legacy) < 1 && Math.abs(c.plan.contribution - manualPlanFigures.contribution) < 1);
    };
    const manualChosen = savedHomeCareStrategies.some(isChosenManualPlan);
    const anotherSahPlanChosen = !manualChosen && savedHomeCareStrategies.some((s) => (s as { chosen?: boolean }).chosen);
    const [manualExplainOpen, setManualExplainOpen] = useState(false);
    const [manualRisksOpen, setManualRisksOpen] = useState(false);
    const [manualCareUpOpen, setManualCareUpOpen] = useState(false);
    const [manualFigurePage, setManualFigurePage] = useState<SahFigurePage | null>(null);
    const sahWho = householdVoice(factFind as never);
    const manualPensionWhy = sahBindingTest === 'assets'
        ? `The Age Pension is worked out under both the assets test and the income test, and the lower result is paid. Here the assets test sets it: Centrelink counts ${formatCurrency(Number(beforeFinancials?.assessableAssets) || 0)} of ${sahWho.possessive} assets today, and each $1,000 less that is counted adds about ${formatCurrency(ANNUAL_REDUCTION_PER_1000_ASSETS)} a year, until the income test takes over.`
        : sahBindingTest === 'income'
            ? 'The Age Pension is worked out under both the assets test and the income test, and the lower result is paid. Here the income test sets it, so moving assets around changes it only where the income they are deemed to earn changes.'
            : 'The Age Pension is worked out under both the assets test and the income test; the lower result is paid.';

    /**
     * "Choose this plan" for a plan built by hand — the engine card's button, the engine card's record
     * (displayName, chosen, baseline), the same service plan stamp. Choosing the same levers again
     * replaces the earlier record rather than adding a second one.
     */
    /*
     * INCOME ONLY NEEDS THE CLIENT'S TICK (owner, 21 Oct 2026): a hand-built plan whose annuity returns
     * nothing cannot be chosen until the client has ticked that nothing comes back. See IncomeOnlyTick.tsx.
     */
    const manualIncomeOnly = selectedFundingStrategy === 'annuity' && isIncomeOnlyAnnuity(annuityRecord);
    /** The hand-built plan's guaranteed income, for its family panel (sahGuaranteedIncome.ts). */
    const manualAnnuity = selectedFundingStrategy === 'annuity' ? sahRecordAnnuityOf(annuityRecord as never, manualPieces.length === 1) : null;
    const manualIncomeOnlyHeld = incomeOnlyHeld(factFind, manualIncomeOnly, Number(annuityRecord?.purchaseAmount) || 0);
    const setIncomeOnlyUnderstood = incomeOnlySetter(setFactFind);
    const chooseManualPlan = () => {
        if (!manualPlanFigures) return;
        if (manualIncomeOnlyHeld) return;
        const earlier = savedHomeCareStrategies.find((s) => (s.inputs as { manualFingerprint?: string } | undefined)?.manualFingerprint === manualFingerprint);
        const strategy = {
            id: earlier?.id || `strategy-${Date.now()}`,
            name: manualWords.title,
            displayName: manualWords.title,
            type: 'home_care',
            source: 'manual',
            // Built by the client from a tested plan ("Customise this plan", 25 Oct 2026): the advice document says so.
            ...(manualSeededFrom ? { customisedFrom: manualSeededFrom } : {}),
            inputs: {
                source: 'manual',
                manualFingerprint,
                legacyYears: Number(legacyYears) || 10,
                gifting: manualStrategies.gifting,
                funeralBonds: manualStrategies.funeralBonds,
                giftingSource: manualStrategies.giftingSource,
                funeralSource: manualStrategies.funeralSource,
                propertyStrategy,
                downsizeDetails: propertyStrategy === 'downsize' ? downsizeDetails : null,
                renovateDetails: propertyStrategy === 'renovate' ? renovateDetails : null,
                upgradeDetails: propertyStrategy === 'upgrade' ? upgradeDetails : null,
                reverseMortgageDetails: (overallStrategy.reverseMortgage || propertyStrategy === 'reverseMortgage') ? reverseMortgageDetails : null,
                heasDetails: (overallStrategy.heas || propertyStrategy === 'heas') ? heasDetails : null,
                selectedFundingStrategy,
                annuityRecord: selectedFundingStrategy === 'annuity' ? annuityRecord : null,
                // The record the annuity was applied with, so the plan's position can be rebuilt (Action Plan forms).
                annuityRecordForCalculation: selectedFundingStrategy === 'annuity' ? annuityRecordForCalculation : null,
                debtPayments,
                debtFunding,
                debtRepaymentBasis,
                agedCareContext: selectedAgedCareStrategyId ? 'linked' : 'current',
                selectedAgedCareStrategyId: selectedAgedCareStrategyId || null,
                // The household it was priced on: the whole aged care plan carried in, or none (sahChosenStale.ts).
                householdFingerprint: sahHouseholdFingerprint(selectedAgedCareStrategyId ? String(selectedAgedCareStrategyId) : null),
                overallStrategy,
                servicePlan: resolvedServicePlan.plan,
                servicePlanFingerprint: JSON.stringify(servicePlanSource ?? {}),
                // The card as chosen — the plan page shows and explains it from this (sahChosenPlan.ts).
                card: sahCardRecord({
                    plan: manualPlanFigures,
                    baseline: manualBaseFigures,
                    words: manualWords,
                    drawLines: abpDrawSentences(abpDrawChangesBetween(factFind, optimizedScenario?.optimizedFactFind)),
                    riskKinds: manualPieces.flatMap((p) => riskKindsFromSupportAtHomeType(String(p.type), String(p.name || ''))),
                    horizonYears: Number(legacyYears) || 10,
                    origin: 'manual',
                    moved: sahMovedAmount(manualPieces.length > 1 ? { type: 'combination', steps: manualPieces } : manualPieces[0]).amount,
                }),
            },
            outputs: {
                // The card's own figures, so Home and the advice document quote what was chosen.
                annualContribution: manualPlanFigures.contribution,
                agePension: manualPlanFigures.pension,
                cashFlow: manualPlanFigures.cashflow,
                cashFlowBasis: 'net_of_service_plan',
                legacyEstimate: manualPlanFigures.legacy,
                netWorthSchedule: buildNetWorthSchedule(projections),
                financials: afterFinancials,
                contributionRates: afterContributionData?.contributionRates || {},
                servicePlanBasis: currentServicePlanBasis,
            },
            baseline: manualBaseFigures
                ? { legacyValue: manualBaseFigures.legacy, pension: manualBaseFigures.pension, cashFlow: manualBaseFigures.cashflow, contribution: manualBaseFigures.contribution }
                : null,
            chosen: true,
            createdAt: earlier?.createdAt || new Date().toISOString(),
        };
        persistHomeCareStrategy(strategy as never);
        // What the advice document needs, captured as it stands now (Get the advice document).
        setManualSoaOffer({ strategy: strategy as never, projections: ((projectionsForModal || projections) ?? null) as never });
        setManualSoaDeferred(true);
    };

    // The plan page asked for the advice document of a plan built by hand: open it once it is priced.
    const manualAdviceRequested = useRef(hubIntent?.kind === 'document');
    useEffect(() => {
        if (!manualAdviceRequested.current || internalStrategyMode !== 'manual' || !manualPlanFigures || !manualChosen) return;
        manualAdviceRequested.current = false;
        void openManualDigitalSoa();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [internalStrategyMode, manualPlanFigures, manualChosen]);

    // The plan page asked for a hand-built plan's year-by-year figures: open them once it is priced.
    const manualYearByYearRequested = useRef(hubIntent?.kind === 'yearByYear');
    useEffect(() => {
        if (!manualYearByYearRequested.current || internalStrategyMode !== 'manual' || !manualPlanFigures) return;
        manualYearByYearRequested.current = false;
        setShowProjectionsModal(true);
    }, [internalStrategyMode, manualPlanFigures]);

    const sahInBrief = useMemo<React.ReactNode | null>(() => {
        if (!sahPlanSummaryName) return null;
        const contribBefore = sahContributionBefore;
        const contribAfter = sahContributionAfter;
        const pensionDelta = (impactAfterFinancials?.socialSecurity ?? 0) - (beforeFinancials?.socialSecurity ?? 0);
        const cashflowDelta = (sahCashFlowAfter ?? 0) - (sahCashFlowBefore ?? 0);
        const legacyDelta =
            sahDoNothingLegacy != null && impactProjectedLegacy != null
                ? impactProjectedLegacy - sahDoNothingLegacy
                : null;

        const clauses: RecapClause[] = [];
        /*
         * NO DOLLARS WITHOUT A SERVICE PLAN. Out-of-pocket is budget x rate, and until a plan exists
         * the budget is the illustrative package's. Quoting "your share falls from $7,722 to $3,929"
         * against services nobody has chosen puts a placeholder in the sentence a client remembers.
         * The RATE movement is real either way, so that is what leads instead.
         */
        if (resolvedServicePlan.isEstimated) {
            const rateBefore = beforeContributionData?.contributionRates?.everydayLiving;
            const rateAfter = afterContributionData?.contributionRates?.everydayLiving;
            if (rateBefore != null && rateAfter != null && rateBefore !== rateAfter) {
                // A FALLING rate is good news here — the client pays a smaller share.
                clauses.push({
                    tone: rateAfter < rateBefore ? 'good' : 'bad',
                    text: `the everyday living rate ${rateAfter < rateBefore ? 'falls' : 'rises'} from ${(rateBefore * 100).toFixed(1)}% to ${(rateAfter * 100).toFixed(1)}% (the dollars follow once a service plan is built)`,
                });
            }
        } else if (contribBefore != null && contribAfter != null && Math.round(contribBefore - contribAfter) !== 0) {
            const down = contribAfter < contribBefore;
            clauses.push({
                term: 'your share of care costs',
                tone: down ? 'good' : 'bad',
                text: `your share of care costs ${down ? 'falls' : 'rises'} from ${formatCurrency(contribBefore)} to ${formatCurrency(contribAfter)} a year`,
            });
        } else if (contribAfter != null) {
            clauses.push(plainClause(`your share of care costs stays at ${formatCurrency(contribAfter)} a year`));
        }
        clauses.push(pensionClause(pensionDelta));
        if (Math.round(cashflowDelta) !== 0) {
            clauses.push(cashflowClause(cashflowDelta, sahCashFlowAfter));
        }
        if (legacyDelta != null) clauses.push(estateClause(legacyDelta, Number(legacyYears) || 0));
        if (!clauses.length) return null;
        return <RecapClauses clauses={clauses} />;
    }, [
        sahPlanSummaryName,
        beforeContributionData,
        afterContributionData,
        impactAfterFinancials,
        beforeFinancials,
        sahDoNothingLegacy,
        impactProjectedLegacy,
        legacyYears,
        resolvedServicePlan,
        sahCashFlowAfter,
        sahCashFlowBefore,
        sahContributionBefore,
        sahContributionAfter,
    ]);
    const rightColumnBlurred = isSuggestionMode && !showBaseline;

    return (
        <>
            {/* Insight Panel */}
            
            {/* THE SAME 3/2 SPLIT AT THE SAME BREAKPOINT AS AGED CARE (2026-08-27). This was
                `lg:grid-cols-3` with a 1/3 rail: at a 1024px viewport that is a ~227px pane for a
                component whose internal breakpoints assume ~391px, so the Financial Impact heading
                and its projections link collapsed onto one line. `xl:` is the benchmark's choice and
                the reasoning is in agedCareResponsiveLayout.test.ts. */}
            {/* The tested plans take the full width, as in aged care (owner, 17 Oct 2026: strip, not rail);
                Manual Control keeps the rail, where the figures move as each lever is set. */}
            <div className={isSuggestionMode ? 'space-y-6' : 'grid grid-cols-1 xl:grid-cols-5 gap-8 items-start'}>
                {/* Left Column - Main Content */}
                <div className={isSuggestionMode ? 'space-y-6' : 'xl:col-span-3 space-y-6'}>
                {/*
                  * WHEN SUPPORT STARTS now lives in the Service Builder, and is stated here rather
                  * than asked again.
                  *
                  * The owner read it as a duplicate; it was not — this was the only copy. But the
                  * point behind it holds: when services begin is a property of the SERVICE PLAN, and
                  * the plan is built first now. Asking on the optimisation screen put a care fact
                  * among the financial levers, one screen after the place it belongs.
                  *
                  * It still matters here — it is the date the Suggestion Engine projects from, and
                  * an engine that silently assumed today would rank every candidate against the
                  * wrong timeline — so the value is read, shown, and linked back to where it is set.
                  */}
                {/* In the three steps (rebuild M3) the start date, the starting position and the service
                    plan are steps 1 and 2 — not repeated here. */}
                {!sahSeAnalyzing && !stepped && (
                    <div className="bg-white rounded-xl border border-gray-200 px-6 py-4 shadow-sm flex flex-wrap items-center justify-between gap-3">
                        <p className="text-sm text-slate-700">
                            <span className="font-semibold text-slate-900">Support services start:</span>{' '}
                            {supportStartDate && !supportStartInPast
                                ? new Date(`${supportStartDate}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
                                : 'from today'}
                            {supportStartInPast && (
                                <span className="ml-2 text-xs text-amber-700">
                                    A past date models as “already receiving support”.
                                </span>
                            )}
                        </p>
                        <button
                            type="button"
                            onClick={() => onNavigateToBuilder?.()}
                            className="text-sm font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
                        >
                            Change it in Service Builder ›
                        </button>
                    </div>
                )}

                {/* The starting position — the anchor every strategy below is compared against,
                    between the case facts and the mode choice (owner direction, 2026-08-25). The
                    insight panel's opening story, as a headline with the depth behind taps. */}
                {/* Fed from the SAME sources the panel beside it reports, so the anchor and the
                    figures it anchors cannot disagree: sahDoNothingLegacy is the estate tile's own
                    "before", and the runway reads the projection rows. */}
                {/* On the tested plans the four-number strip is the starting position. */}
                {!sahSeAnalyzing && !isSuggestionMode && !stepped && (
                    <SahStartingPosition
                        factFind={factFind}
                        before={beforeFinancials}
                        careContribution={sahContributionBefore != null ? { amount: sahContributionBefore, planName: servicePlanName } : null}
                        runwayHeadline={startingRunway?.headline ?? null}
                        runwayDetail={startingRunway?.flag ?? null}
                        startingLegacy={sahDoNothingLegacy}
                        legacyYears={legacyYears}
                        onLegacyYearsChange={setLegacyYears}
                    />
                )}

                {/* WHICH service plan the figures use — identified by size, with the
                    chicken-and-egg said in one reassuring sentence (owner copy pass, 2026-08-25). */}
                {/*
                  * The service plan every figure on this screen is priced on — given the same card
                  * anatomy as "Where Baldo stands today" (owner, 2026-08-27: "I think we can make
                  * this tile look much nicer... include a heading like we do for Where Baldo stands
                  * today"). It reads like a footnote and it is not one: it names the plan the whole
                  * screen depends on, and whether that plan is real or illustrative.
                  */}
                {!sahSeAnalyzing && !stepped && (
                    <PlainWordsCard
                        eyebrow={resolvedServicePlan.isEstimated ? 'What these figures are priced on' : 'Your service plan'}
                        className="mb-0"
                    >
                        <PlainWordsBeat
                            /* Amber earns its place only when the basis is a placeholder — that IS a
                               "check this". A real plan is a plain statement of fact. */
                            tone={resolvedServicePlan.isEstimated ? 'warn' : 'info'}
                            heading={
                                resolvedServicePlan.isEstimated
                                    ? 'No service plan built yet — an illustrative package is standing in'
                                    : 'Priced on the plan you built'
                            }
                        >
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <p className="min-w-0 leading-relaxed text-slate-700">
                                {resolvedServicePlan.isEstimated ? (
                                    <>
                                        <span className="font-semibold text-slate-900">No service plan built yet</span> — these
                                        figures use an illustrative typical package as the basis
                                        {resolvedServicePlan.description && (
                                            <> <JargonTerm term="(what's in it?)">{resolvedServicePlan.description}</JargonTerm></>
                                        )}.
                                    </>
                                ) : (
                                    <>
                                        These figures are priced on{' '}
                                        <span className="font-semibold text-slate-900">
                                            {servicePlanResolution.origin.kind === 'current'
                                                ? servicePlanResolution.origin.basedOnName
                                                    ? `the saved plan “${servicePlanResolution.origin.basedOnName}” (loaded in Service Builder)`
                                                    : 'the services currently selected in Service Builder (not yet saved as a plan)'
                                                : servicePlanResolution.origin.kind === 'saved'
                                                  ? `the saved plan \u201c${servicePlanResolution.origin.name}\u201d${servicePlanResolution.origin.savedCount > 1 ? ` (most recent of ${servicePlanResolution.origin.savedCount} saved)` : ''}`
                                                  : servicePlanResolution.origin.kind === 'restore'
                                                    ? servicePlanResolution.origin.name
                                                    : 'the service plan built in Service Builder'}
                                        </span>
                                        {servicePlanStats && servicePlanStats.serviceCount > 0 && (
                                            <>
                                                {' '}— {servicePlanStats.serviceCount}{' '}
                                                {servicePlanStats.serviceCount === 1 ? 'service' : 'services'}, about{' '}
                                                <span className="font-semibold text-slate-900">
                                                    {formatCurrency(servicePlanStats.annualValue)}/yr
                                                </span>{' '}
                                                of care
                                            </>
                                        )}
                                        .
                                    </>
                                )}
                            </p>
                            <button
                                type="button"
                                onClick={onNavigateToBuilder}
                                className="shrink-0 text-sm font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
                            >
                                {resolvedServicePlan.isEstimated ? 'Build the service plan' : 'Change it in Service Builder'} ›
                            </button>
                        </div>
                        {emptySavedServicePlans.length > 0 && (
                            <p className="mt-2 text-xs text-amber-700">
                                {emptySavedServicePlans.map((p) => `“${p.name}”`).join(', ')}{' '}
                                {emptySavedServicePlans.length === 1 ? 'has' : 'have'} no services in {emptySavedServicePlans.length === 1 ? 'it' : 'them'}, so{' '}
                                {emptySavedServicePlans.length === 1 ? 'it' : 'they'} can&apos;t be priced — open the Service Builder, add services and save again.
                            </p>
                        )}
                        {savedServicePlans.length > 0 && (
                            <label className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                                Service plan:
                                {/*
                                  * SHOWS WHAT IS SELECTED, AND CAN SELECT NOTHING (owner-caught,
                                  * 2026-08-27). It was pinned to value="" — a permanent "choose…"
                                  * that never reflected the plan in use, so the control and the
                                  * sentence above it disagreed about the same fact. And having
                                  * loaded a plan there was no way back to none, which is a real
                                  * state: it is what a client sees before building anything, and
                                  * it is the "current position" the panel describes.
                                  */}
                                <select
                                    value={selectedSavedPlanId}
                                    onChange={(e) => {
                                        if (e.target.value === '') {
                                            clearCurrentServicePlan();
                                            setPlanPickVersion((v) => v + 1);
                                            return;
                                        }
                                        const picked = savedServicePlans.find((p) => p.id === e.target.value);
                                        if (!picked) return;
                                        setCurrentServicePlan(picked.plan, picked.name);
                                        setPlanPickVersion((v) => v + 1);
                                    }}
                                    className="rounded-lg border border-gray-300 px-2 py-1 text-xs"
                                >
                                    <option value="">No service plan — my current position</option>
                                    {savedServicePlans.map((p) => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                    {selectedSavedPlanId === UNSAVED_PLAN_OPTION && (
                                        /* Selections exist in the builder that were never saved. Naming
                                           them keeps the control honest rather than showing "none". */
                                        <option value={UNSAVED_PLAN_OPTION}>Services selected in Service Builder (not saved)</option>
                                    )}
                                </select>
                            </label>
                        )}
                        {/* The nudge reversed with the tab order (2026-08-27). It used to present the
                            two orders as equal, which was true of the ADVICE and not of the figures:
                            the contribution rates hold whatever services are chosen, but the dollars
                            they apply to are the illustrative package's until a real plan exists. So
                            this now says which half is provisional rather than implying neither is. */}
                        <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
                            You can optimise from here — the <span className="font-medium text-slate-700">contribution rates</span>{' '}
                            below are set by the means assessment and hold whatever services are chosen. The{' '}
                            <span className="font-medium text-slate-700">dollar amounts</span> are the illustrative
                            package&apos;s until a real plan is built, so build the service plan when you can and these
                            figures re-price against it.
                        </p>
                        </PlainWordsBeat>
                    </PlainWordsCard>
                )}

                {/* NO MODE CHOOSER (owner, 19 Oct 2026; aged care's ruling of 17 Oct). "Manual Control or
                    Suggestion Engine?" was the first thing this step asked, and a family cannot answer it.
                    The tested plans are the step; "Build a plan yourself" under them opens Manual Control,
                    seeded from the plan it was opened from, with a way back. */}
                {internalStrategyMode === 'manual' && (
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-card" data-testid="sah-manual-banner">
                        <p className="text-[14px] text-slate-700">
                            {manualSeededFrom
                                ? <><span className="font-semibold text-slate-900">Customising &ldquo;{manualSeededFrom}&rdquo;.</span>{' '}Change any piece; the figures on the right move as you do.</>
                                : <><span className="font-semibold text-slate-900">Building a plan yourself.</span>{' '}Set each piece; the figures on the right move as you do.</>}
                        </p>
                        <div className="flex flex-wrap items-center gap-4">
                            <button
                                type="button"
                                onClick={() => buildFromEnginePlan(null)}
                                className="text-[13px] font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900"
                                data-testid="sah-clear-start-again"
                            >
                                Clear and start again
                            </button>
                            <button
                                type="button"
                                onClick={() => { handleStrategyModeChange('suggestion'); scrollAppToTop(); }}
                                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900"
                                data-testid="sah-back-to-plans"
                            >
                                ‹ Back to the tested plans
                            </button>
                        </div>
                    </div>
                )}

                {/* The tested plans */}
                {internalStrategyMode === 'suggestion' && (
                    <SupportAtHomeSuggestionEnginePanel
                        onSoaGenerated={onSoaGenerated}
                        supportStartDate={supportStartInPast ? '' : supportStartDate}
                        factFind={factFind}
                        setFactFind={setFactFind}
                        // getServicePlanFromStorage's StoredServicePlan is deliberately loose (JSON.parse'd
                        // from sessionStorage); SupportAtHomeServicePlan is the strict in-memory shape the
                        // panel expects — same runtime data, structurally-incompatible declared types.
                        servicePlan={servicePlanSource as SupportAtHomeServicePlan} // The SAME source the manual mode and the case line use
                        goals={factFind.goals || {}}
                        onStrategySelect={(strategy: Record<string, unknown>) => {
                            setSahSeSelectedStrategy(strategy);
                        }}
                        selectedStrategy={sahSeSelectedStrategy}
                        legacyYears={legacyYears}
                        onLegacyYearsChange={setLegacyYears}
                        bindingTest={sahBindingTest}
                        onBuildYourOwn={buildFromEnginePlan}
                        openAdviceWhenReady={hubIntent?.kind === 'document'}
                        openYearByYearWhenReady={hubIntent?.kind === 'yearByYear'}
                        onNavigateToDashboard={() => onNavigateToDashboard?.()}
                        autoOpenOnMount={true}
                        selectedAgedCareIntegration={selectedAgedCareIntegration}
                        onGoToServiceBuilder={onNavigateToBuilder}
                        showBaseline={showBaseline}
                        setShowBaseline={setShowBaseline}
                        homeCareSaveContext={{
                            participantType,
                            nwopCategory,
                            meansDisclosed,
                            previousITCF,
                            isPartnered,
                        }}
                        onAnalysisStateChange={setSahSeAnalyzing}
                    />
                )}
                
                {/* Manual Control Content */}
                {internalStrategyMode === 'manual' && (
                    <div className="space-y-6">
                        {/* Integration Strategy - Partner's Aged Care Scenario - FIRST */}
                        {hasAgedCareStrategies && (
                            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                                <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <Users className="w-5 h-5 text-blue-600" />
                                    {(() => { const e = sahEnteringPerson(factFind); const n = e ? String((e === 'client' ? factFind?.client?.firstName : factFind?.partner?.firstName) || '').trim() : ''; return n ? `${n}'s aged care plan` : 'The aged care plan'; })()}
                                </h4>
                                <p className="text-sm text-gray-600 mb-4">
                                    Choose a saved aged care plan to include the deposit it pays and its yearly fees in these figures. Everything below then follows it.
                                </p>
                                <div className="space-y-3">
                                    <label className="text-sm font-medium text-gray-700">Which aged care plan</label>
                                    <select
                                        value={selectedAgedCareStrategyId || ''}
                                        onChange={(event) => setSelectedAgedCareStrategyId(event.target.value || null)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">None — leave aged care out</option>
                                        {availableAgedCareStrategies.map((strategy) => (
                                            <option key={strategy.id} value={strategy.id}>
                                                {savedPlanTitle(strategy as never, factFind as never)} – care {formatCurrency(strategy.outputs?.agedCareCost || 0)} a year
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {selectedAgedCareIntegration && (
                                    <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-4">
                                        <div className="flex flex-wrap gap-6 text-sm text-blue-900">
                                            <div className="flex-1 min-w-[160px]">
                                                <p className="font-semibold text-blue-800">Annual aged care fees</p>
                                                <p className="text-xl font-bold">{formatCurrency(selectedAgedCareIntegration.annualCost)}</p>
                                            </div>
                                            <div className="flex-1 min-w-[160px]">
                                                <p className="font-semibold text-blue-800">RAD paid upfront</p>
                                                <p className="text-xl font-bold">{formatCurrency(selectedAgedCareIntegration.radPaid || 0)}</p>
                                            </div>
                                        </div>
                                        <div className="mt-4 text-xs text-blue-700 flex items-center gap-2">
                                            <AlertCircle className="w-4 h-4" />
                                            We automatically reduce the household assets by the RAD funding and subtract the ongoing daily fees from cashflow.
                                        </div>
                                        {/* The other half of what linking does — the Age Pension reassessment
                                            (owner-caught omission, 2026-08-25): the fees and RAD were stated, the
                                            reassessment that usually dwarfs them was not. */}
                                        {integrationSummary && (
                                            <p className="mt-2 text-xs leading-relaxed text-blue-900">
                                                Linking this also changes the <strong>Age Pension assessment</strong>: with{' '}
                                                {integrationSummary.personName} in residential care
                                                {integrationSummary.illnessSeparated && (
                                                    <>
                                                        , the household is assessed as an{' '}
                                                        <JargonTerm term="illness-separated couple" /> — each paid at the
                                                        higher single rate, with more generous thresholds
                                                    </>
                                                )}
                                                {integrationSummary.radPaid > 0 && (
                                                    <>
                                                        , and the {formatCurrency(integrationSummary.radPaid)} RAD is exempt
                                                        from the pension assets test
                                                    </>
                                                )}
                                                . The Age Pension figure on the right already includes this.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        
                        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Gift className="w-5 h-5 text-blue-600" />
                                Gifts
                            </h4>
                            <p className="text-sm text-gray-600 mb-4">
                                Centrelink allows you to gift up to $10,000 per year and $30,000 over a rolling 5-year period. 
                                Gifts within these thresholds are not included in your asset test.
                            </p>
                            <div className="flex items-center gap-4">
                                <CurrencyInput
                                    label="Annual Gifting Amount:"
                                    value={manualStrategies.gifting ?? ''}
                                    onChange={(v) => setManualStrategies({...manualStrategies, gifting: v ?? 0})}
                                    className="w-40"
                                />
                            </div>
                            <GiftingFieldNote
                                giftingAmount={Number(manualStrategies.gifting) || 0}
                                giftsHistory={factFind.giftsHistory}
                            />
                            {Number(manualStrategies.gifting) > 0 && (
                                <div className="mt-4">
                                    <label className="text-sm font-medium text-gray-700">Source of funds:</label>
                                    <select
                                        value={manualStrategies.giftingSource}
                                        onChange={(e) => setManualStrategies({...manualStrategies, giftingSource: e.target.value})}
                                        className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="bank">From Bank Account</option>
                                        <option value="abp">From Account-Based Pension</option>
                                        <option value="shares">From Shares Portfolio</option>
                                    </select>
                                </div>
                            )}
                        </div>
                        
                        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <ShieldCheck className="w-5 h-5 text-blue-600" />
                                Funeral Bonds
                            </h4>
                            <p className="text-sm text-gray-600 mb-4">
                                Funeral bonds are excluded from asset testing for both the Age Pension and Support at Home
                                assessments — but only up to {formatCurrency(FUNERAL_BOND_LIMIT)} per person (indexed each
                                year). Above that the exemption is lost, so there is no strategy benefit in going higher.
                            </p>
                            {(() => {
                                /*
                                 * Capped at the household's REMAINING exemption headroom (owner-caught,
                                 * 2026-08-25): unlike gifting — where exceeding the free areas is a
                                 * legitimate choice the engine prices as deprivation — a bond above the
                                 * limit simply isn't exempt, so the input never offers a move with no
                                 * upside. Bonds already owned use up headroom first.
                                 */
                                const existingBonds = (factFind?.assets?.funeralBonds || []).reduce(
                                    (sum: number, bond: { value?: number | string }) => sum + (parseFloat(String(bond?.value)) || 0),
                                    0,
                                );
                                const headroom = Math.max(0, FUNERAL_BOND_LIMIT * (isPartnered ? 2 : 1) - existingBonds);
                                return (
                                    <div>
                                        <div className="flex items-center gap-4">
                                            <CurrencyInput
                                                label="Additional Funeral Bond Purchase:"
                                                value={manualStrategies.funeralBonds ?? ''}
                                                onChange={(v) => setManualStrategies({ ...manualStrategies, funeralBonds: Math.min(v ?? 0, headroom) })}
                                                className="w-40"
                                            />
                                        </div>
                                        <p className="mt-1 text-xs text-slate-500">
                                            Room left within the exemption: {formatCurrency(headroom)}
                                            {existingBonds > 0 && <> ({formatCurrency(existingBonds)} of bonds already held)</>}.
                                        </p>
                                    </div>
                                );
                            })()}
                            {Number(manualStrategies.funeralBonds) > 0 && (
                                <div className="mt-4">
                                    <label className="text-sm font-medium text-gray-700">Source of funds:</label>
                                    <select
                                        value={manualStrategies.funeralSource}
                                        onChange={(e) => setManualStrategies({...manualStrategies, funeralSource: e.target.value})}
                                        className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="bank">From Bank Account</option>
                                        <option value="abp">From Account-Based Pension</option>
                                        <option value="shares">From Shares Portfolio</option>
                                    </select>
                                </div>
                            )}
                        </div>
                        
                        {/* Property Strategy Options - Only show if client owns their home */}
                        {anyoneOwnsHome(factFind as unknown as FactFind) && (
                            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                                <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <Home className="w-5 h-5 text-blue-600" />
                                    Your home
                                </h4>
                                <p className="text-sm text-gray-600 mb-4">
                                    Choose how you would like to release equity from your home. Select one option below to configure the details.
                                </p>

                                <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-5">
                                    {[
                                        {
                                            id: 'downsize',
                                            title: 'Downsize family home',
                                            description: 'Sell current home, buy lower-cost property, redirect surplus capital.'
                                        },
                                        {
                                            id: 'renovate',
                                            title: 'Renovate home',
                                            description: 'Home improvements convert assessable assets into exempt home equity.'
                                        },
                                        {
                                            id: 'upgrade',
                                            title: 'Upgrade home',
                                            description: 'Buy more expensive home to convert assessable assets into exempt home equity.'
                                        },
                                        {
                                            id: 'reverseMortgage',
                                            title: 'Reverse mortgage',
                                            description: 'Borrow against equity in home to fund needs.'
                                        },
                                        {
                                            id: 'heas',
                                            title: 'Home Equity Access Scheme',
                                            description: 'Government program providing fortnightly payments secured on your home.'
                                        }
                                    ].map(option => {
                                        const isSelected = propertyStrategy === option.id;
                                        return (
                                            <button
                                                key={option.id}
                                                type="button"
                                                onClick={() => setPropertyStrategy(option.id)}
                                                className={`text-left border rounded-lg p-4 transition-all duration-200 h-full flex flex-col ${
                                                    isSelected ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/40'
                                                }`}
                                            >
                                                <div className="flex items-start gap-2 mb-4 h-[4rem]">
                                                    {option.id === 'downsize' && <Home className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />}
                                                    {option.id === 'renovate' && <Wrench className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />}
                                                    {option.id === 'upgrade' && <TrendingUp className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />}
                                                    {option.id === 'reverseMortgage' && <TrendingDown className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />}
                                                    {option.id === 'heas' && <ShieldCheck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />}
                                                    <span className="font-semibold text-gray-900 leading-tight">{option.title}</span>
                                                </div>
                                                <p className="text-xs text-gray-600 leading-relaxed flex-grow mb-3">{option.description}</p>
                                                {isSelected && (
                                                    <div className="mt-auto text-xs font-medium text-blue-700 pt-2 border-t border-blue-200">Selected</div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>

                                {propertyStrategy !== 'none' && (
                                    <div className="mt-3 text-xs text-gray-500">
                                        Want to remove this strategy?{' '}
                                        <button
                                            type="button"
                                            onClick={() => setPropertyStrategy('none')}
                                            className="text-blue-600 underline"
                                        >
                                            Clear selection
                                        </button>
                                    </div>
                                )}

                                {propertyStrategy === 'none' && (
                                    <div className="mt-4 text-xs text-gray-500">
                                        Select a property strategy to see the detailed inputs.
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Property Strategy Inputs - Show when a strategy is selected */}
                        {propertyStrategy && propertyStrategy !== 'none' && anyoneOwnsHome(factFind as unknown as FactFind) && (
                            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                                {propertyStrategy === 'downsize' && (
                                    <SupportDownsizeStrategyAllocator
                                        factFind={householdFactFind}
                                        details={downsizeDetails}
                                        setDetails={setDownsizeDetails}
                                    />
                                )}

                                {/* Both schemes repay nothing along the way, so the number that decides
                                    the family's outcome is what is OWED at the end. The same shared
                                    notes Aged Care uses — these are means-test levers, not care ones. */}
                                {propertyStrategy === 'reverseMortgage' && (
                                    <>
                                        <ReverseMortgageStrategyAllocator
                                            factFind={householdFactFind}
                                            details={reverseMortgageDetails}
                                            setDetails={setReverseMortgageDetails}
                                            legacyYears={legacyYears}
                                        />
                                        <ReverseMortgageFieldNote
                                            loanAmount={Number(reverseMortgageDetails?.loanAmount) || 0}
                                            interestRate={Number(reverseMortgageDetails?.interestRate) || 0}
                                            years={Number(legacyYears) || 5}
                                        />
                                    </>
                                )}

                                {propertyStrategy === 'heas' && (
                                    <>
                                        <HEASStrategyAllocator
                                            factFind={householdFactFind}
                                            details={heasDetails}
                                            setDetails={setHeasDetails}
                                            legacyYears={legacyYears}
                                        />
                                        <HeasFieldNote
                                            paymentAmount={Number(heasDetails?.paymentAmount) || 0}
                                            paymentFrequency={String(heasDetails?.paymentFrequency || 'fortnightly')}
                                            interestRate={Number(heasDetails?.interestRate) || 0}
                                            years={Number(legacyYears) || 5}
                                        />
                                    </>
                                )}

                                {propertyStrategy === 'renovate' && (
                                    <RenovateStrategyAllocator
                                        factFind={householdFactFind}
                                        details={renovateDetails}
                                        setDetails={setRenovateDetails}
                                        beforeFinancials={beforeFinancials ?? undefined}
                                    />
                                )}

                                {propertyStrategy === 'upgrade' && (
                                    <UpgradeStrategyAllocator
                                        factFind={householdFactFind}
                                        details={upgradeDetails}
                                        setDetails={setUpgradeDetails}
                                        beforeFinancials={beforeFinancials ?? undefined}
                                    />
                                )}
                            </div>
                        )}
                        
                        {/* Additional Funding & Pension Strategies */}
                        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                            <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <ShieldCheck className="w-5 h-5 text-blue-600" />
                                Other options
                            </h4>
                            <p className="text-sm text-gray-600 mb-4">
                                These optional strategies reshape your assets and income so you may qualify for more Age Pension support and reduce out-of-pocket care fees.
                            </p>

                            <div className="grid gap-3 md:grid-cols-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        const newValue = selectedFundingStrategy === 'annuity' ? null : 'annuity';
                                        setSelectedFundingStrategy(newValue);
                                        if (newValue === 'annuity' && !annuityRecord) {
                                            setAnnuityRecord(initializeAnnuityRecord());
                                        } else if (newValue === null) {
                                            setAnnuityRecord(null);
                                        }
                                    }}
                                    className={`text-left border rounded-lg p-4 transition-all duration-200 ${selectedFundingStrategy === 'annuity' ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/40'}`}
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <ShieldCheck className="w-5 h-5 text-blue-600" />
                                        <span className="font-semibold text-gray-900">Commence an annuity</span>
                                        {selectedFundingStrategy === 'annuity' && (
                                            <Badge tone="info" className="ml-auto">Selected</Badge>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-600 mb-2">
                                        Convert savings into guaranteed income. May reduce assessable assets and help cover ongoing care costs.
                                    </p>
                                    <div className="flex gap-2 flex-wrap text-[11px]">
                                        <Badge tone="info">Age Pension Focus</Badge>
                                        <Badge tone="neutral">Cashflow Stability</Badge>
                                    </div>
                                </button>
                            </div>

                            {selectedFundingStrategy === 'annuity' ? (
                                <div className="mt-4">
                                    <SupportAtHomeAnnuityConfigurator
                                        factFind={optimizedScenario?.optimizedFactFind || factFind}
                                        annuityDetails={annuityRecord}
                                        setAnnuityDetails={(updater) => setAnnuityRecord(prev => {
                                            const base: AnnuityDetails = prev ?? initializeAnnuityRecord();
                                            return typeof updater === 'function' ? (updater as (p: AnnuityDetails) => AnnuityDetails)(base) : updater;
                                        })}
                                        annuitySummary={annuitySummary}
                                        annuityWarnings={annuityWarnings}
                                        fundingSources={availableAnnuityFundingSources}
                                        formatCurrency={formatCurrency}
                                        clientAge={factFind?.client?.dob ? calculateAge(factFind.client.dob) : null}
                                        partnerAge={isPartnered && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null}
                                        onIncomeOnlyUnderstoodChange={setIncomeOnlyUnderstood}
                                        // Above the client's ceiling, a caution (not a block): the same money the tested plans size against.
                                        ceilingInvestable={sahAnnuityInvestable(factFind, Number((factFind?.goals as { sahEmergencyReserve?: unknown } | undefined)?.sahEmergencyReserve) || undefined).amount}
                                        ceilingFactFind={factFind}
                                    />
                                </div>
                            ) : (
                                <div className="mt-4 text-xs text-gray-500">
                                    Select a strategy above to view the explainer and customise the details.
                                </div>
                            )}
                        </div>

                        {/* Shared debt strategy card — renders nothing when the client has no debt. */}
                        <DebtPayoffStrategy
                            factFind={householdFactFind as never}
                            debtPayments={debtPayments}
                            setDebtPayments={setDebtPayments}
                            debtFunding={debtFunding}
                            setDebtFunding={setDebtFunding}
                            repaymentBasis={debtRepaymentBasis}
                            setRepaymentBasis={setDebtRepaymentBasis}
                            competingUseNote="The money comes from the same assets that fund your care and living costs at home, so clearing a debt trades the ongoing repayments for a smaller buffer."
                        />

                        {/* THE PLAN AS BUILT. Its result, Explain this, What could go wrong and Choose are in the rail
                            beside the controls (owner, 25 Oct 2026: the card that used to sit here, under every
                            slider, went — nobody saw it without scrolling, and the rail already moved with each
                            change). What stays here is the detail of a guaranteed income or a loan against the
                            home: what the family gets back, and the loan's terms. */}
                        {hasStrategies && manualPlanFigures && (manualAnnuity || manualEquityRelease) && (
                            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card" data-testid="sah-manual-plan-detail">
                                <h4 className="text-lg font-semibold text-gray-900 mb-3">{manualEquityRelease && !manualAnnuity ? 'The loan against the home' : 'What the family gets back'}</h4>

                                        {/* The loan against the home, on the card itself (22 Oct 2026). It used to be passed only to
                                            the old recap dialog, which nothing opened, so a hand-built plan that borrowed against the
                                            home showed no loan terms at all. Measured against the same projection as the card. */}
                                        {manualEquityRelease && (
                                            <EquityReleaseTerms
                                                className="mb-3"
                                                kind={manualEquityRelease.kind}
                                                loanAmount={manualEquityRelease.loanAmount}
                                                interestRate={manualEquityRelease.interestRate || null}
                                                projectedBalance={manualEquityRelease.projectedBalance || null}
                                                years={manualEquityRelease.years || null}
                                                homeValue={Number(factFind?.home?.value) || null}
                                            />
                                        )}
                                        {manualAnnuity && <AnnuityFamilyPanel
                                            factFind={factFind}
                                            personName={sahAnnuityPersonName(factFind, manualAnnuity.owner)}
                                            amount={manualAnnuity.amount}
                                            kind={manualAnnuity.kind}
                                            incomeAYear={planAnnuityIncomeOf(projections as never, factFind) ?? (Number(annuitySummary?.annualPayment) || null)}
                                            rows={projections as never}
                                            // The support alone is the same plan without it only when the guaranteed income is the one piece set.
                                            compareRows={manualAnnuity.only && sahDoNothingRun.rows?.length ? sahDoNothingRun.rows as never : null}
                                            compareNote={manualAnnuity.only ? SAH_COMPARE_NOTE : null}
                                            horizonYears={Number(legacyYears) || 10}
                                            testId="sah-manual-annuity-family-panel"
                                        />}
                            </div>
                        )}

                        {manualExplainOpen && manualPlanFigures && (
                            <PlanExplanationModal
                                eyebrow="Explain this"
                                title={manualWords.title}
                                intro={`Each line says what ${sahWho.they} do, and what that does to the numbers. ${SAH_BENCH.sentence}`}
                                onClose={() => setManualExplainOpen(false)}
                                footer={<Button type="button" variant="secondary" onClick={() => { setManualExplainOpen(false); setShowProjectionsModal(true); }}>See the year-by-year figures</Button>}
                            >
                                <SahCauseEffect
                                    doSentence={`${manualWords.title}${manualWords.sub ? ` — ${manualWords.sub.replace(/^Paid from /, 'paid from ').replace(/\.$/, '')}` : ''}.`}
                                    treatment={manualWords.treatment}
                                    leverKinds={sahLeverKinds((manualPieces.length > 1 ? { type: 'combination', steps: manualPieces } : manualPieces[0]) as SahConfigLike)}
                                    plan={manualPlanFigures}
                                    baseline={manualBaseFigures}
                                    horizonYears={Number(legacyYears) || 10}
                                    possessive={sahWho.their}
                                    they={sahWho.they}
                                    drawLines={abpDrawSentences(abpDrawChangesBetween(factFind, optimizedScenario?.optimizedFactFind))}
                                    debtsNote={<DebtsInPlanNote factFind={factFind as DraftFactFind} clearedAmount={sahDebtClearedBy((manualPieces.length > 1 ? { type: 'combination', steps: manualPieces } : manualPieces[0]) as never)} years={Number(legacyYears) || 10} context="manual" />}
                                    moneyStory={(() => {
                                        // Where a short year is drawn from, account by account, off the built plan's own rows.
                                        const rows = (projections || []) as unknown[];
                                        const story = summariseFunding(rows as never).hasDeficit
                                            ? buildMoneyStory({ rows: rows as never, factFind: factFind as DraftFactFind, runway: planRunwayCopy(rows as never, rows.length), deposit: { paid: 0, refundTo: '' }, horizonYears: Number(legacyYears) || 10, enteringName: factFind?.client?.firstName || 'The household', spentOn: 'the plan' })
                                            : null;
                                        return story ? <MoneyStoryBlock story={story} testId="sah-money-story" /> : null;
                                    })()}
                                />
                            </PlanExplanationModal>
                        )}
                        {manualFigurePage && manualPlanFigures && (
                            <SahFigurePopover
                                planTitle={manualWords.title}
                                plan={manualPlanFigures}
                                baseline={manualBaseFigures}
                                initialPage={manualFigurePage}
                                possessive={sahWho.their}
                                they={sahWho.they}
                                them={sahWho.them}
                                pensionWhy={manualPensionWhy}
                                onYearByYear={() => { setManualFigurePage(null); setShowProjectionsModal(true); }}
                                onClose={() => setManualFigurePage(null)}
                            />
                        )}
                        {manualCareUpOpen && optimizedScenario?.optimizedFactFind && (
                            <SahCareUpDialog
                                factFind={factFind}
                                setFactFind={setFactFind}
                                servicePlan={(servicePlanSource || { client: {}, partner: {} }) as never}
                                years={Number(legacyYears) || 10}
                                supportStartDate={supportStartDate}
                                savedStrategies={availableAgedCareStrategies}
                                planTitle={manualWords.title}
                                strategyConfig={null}
                                planFactFind={optimizedScenario.optimizedFactFind}
                                onClose={() => setManualCareUpOpen(false)}
                            />
                        )}
                        {manualRisksOpen && (
                            <StrategyRisksPanel
                                strategyName={manualWords.title}
                                voice={sahWho}
                                onClose={() => setManualRisksOpen(false)}
                                risks={buildStrategyRisks(manualPieces.flatMap((p) => riskKindsFromSupportAtHomeType(String(p.type), String(p.name || ''))))}
                            />
                        )}
                    </div>
                )}
                </div>
            
            {/* Right Column - Current Contribution Rates & Financial Impact */}
            <div className={isSuggestionMode ? '' : 'xl:col-span-2 space-y-6 xl:sticky xl:top-8 xl:self-start'}>
                {!isSuggestionMode && (
                /*
                  * ONE PANEL, the same artifact Aged Care uses (owner, 2026-08-27: "I want the
                  * UI/UX experience consistent across the modules").
                  *
                  * This rail held two cards that had drifted apart from the rest of the app: a
                  * blue/teal gradient block of three large contribution tiles, and — the bigger
                  * problem — a HAND-ROLLED copy of the Financial Impact panel, with its own heading,
                  * its own legacy-years control and its own tile grid. A copy cannot inherit a fix:
                  * the compact-row layout, the embedded chrome rules and the tile alignment work all
                  * landed in FinancialImpactPanel and never reached here.
                  *
                  * So the copy is gone and the shared component is used, inside the shared
                  * PlanSummaryPanel. The contribution RATES take the "what care costs" slot, which
                  * is what they are in this module — Support at Home charges a share of each service
                  * rather than a daily fee, so the rates are the price.
                  */
                <PlanSummaryPanel
                    working={sahRailWorking}
                    // The card's own words for the plan, so the rail and the card name it the same way.
                    strategyName={sahPlanSummaryName ? (manualPieces.length || selectedAgedCareIntegration ? manualWords.title : sahPlanSummaryName) : null}
                    baselineLabel={sahDoNothingLegacy != null ? SAH_BENCH.word : undefined}
                    inBrief={sahInBrief}
                    /* The same cause-and-effect telling as the card's Explain this (one telling, as in aged care). */
                    onReadFullExplanation={sahPlanSummaryName && manualPlanFigures ? () => setManualExplainOpen(true) : undefined}
                    onWhatCouldGoWrong={hasStrategies && manualPlanFigures ? () => setManualRisksOpen(true) : undefined}
                    onViewProjections={() => setShowProjectionsModal(true)}
                    /* The plan's actions, where its figures are (owner, 25 Oct 2026): the card under the controls went. */
                    actions={hasStrategies && manualPlanFigures ? (
                        <RailPlanActions
                            testId="sah-manual"
                            chosen={manualChosen}
                            anotherChosen={anotherSahPlanChosen}
                            canChoose={!manualIncomeOnlyHeld}
                            onChoose={chooseManualPlan}
                            beforeChoose={manualIncomeOnly && setIncomeOnlyUnderstood ? (
                                <IncomeOnlyBeforeChoose factFind={factFind} amount={Number(annuityRecord?.purchaseAmount) || 0} onChange={setIncomeOnlyUnderstood} />
                            ) : null}
                            possessiveFile={sahWho.possessive}
                            onAdvice={() => { void openManualDigitalSoa(); }}
                            // "If care needs increase" on a plan built by hand too; not for a loan against the home,
                            // whose terms ride the run's options rather than the position.
                            extra={propertyStrategy === 'reverseMortgage' || propertyStrategy === 'heas' ? null : (
                                <button type="button" onClick={() => setManualCareUpOpen(true)} className="mt-3 text-[13px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4 hover:decoration-brand-navy" data-testid="sah-care-up-open">If care needs increase</button>
                            )}
                        />
                    ) : undefined}
                    /*
                     * SAY WHAT THIS IS (owner-caught, 2026-08-27). Without a service plan these are
                     * not a baseline — they are the client's position today, and the illustrative
                     * package is standing in for services nobody has chosen. Calling that "the
                     * baseline" pointed the reader at a placeholder as the thing to beat.
                     *
                     * With a plan built, the no-strategy state IS the benchmark, exactly as it is in
                     * Aged Care, so the default wording returns.
                     */
                    noStrategyHeading={
                        resolvedServicePlan.isEstimated ? 'No service plan yet — your position today' : 'No plan yet — the starting position'
                    }
                    noStrategyNote={
                        resolvedServicePlan.isEstimated ? (
                            <>
                                This is your <span className="font-semibold text-slate-700">current position</span>.
                                Build the service plan and it becomes the plan every strategy here is measured against.
                            </>
                        ) : undefined
                    }
                    costOverview={panelContributionData ? (
                        <div className="bg-gray-800 text-white rounded-xl p-4">
                            {/*
                              * THE RATES ARE REAL; THE DOLLARS ARE NOT — until a service plan exists.
                              *
                              * Out-of-pocket is budget x rate. The rates come from the means
                              * assessment, hold whatever services are chosen, and are the thing
                              * Optimise actually moves — so they are shown, and they are true now.
                              * The budget does not exist until a plan is built, so quoting a dollar
                              * share would be the illustrative package's figure wearing the client's
                              * name: the same fault as the Suggestion Engine inventing a room price.
                              * Blanking the whole tile would throw away the half that IS true.
                              */}
                            <div className="flex items-start justify-between gap-3 mb-3 pb-3 border-b border-white/15">
                                <p className="text-gray-400 text-xs leading-snug">Your share<br />of care costs</p>
                                <div className="text-right">
                                    {resolvedServicePlan.isEstimated ? (
                                        <>
                                            <p className="text-lg font-bold text-white leading-tight">Needs a service plan</p>
                                            <button
                                                type="button"
                                                onClick={() => onNavigateToBuilder?.()}
                                                className="text-xs font-semibold text-blue-300 underline underline-offset-2 hover:text-blue-200"
                                            >
                                                Build it in Service Builder ›
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <p className="text-3xl font-bold text-white leading-none">
                                                {formatCurrency(sahContributionAfter ?? 0)}
                                            </p>
                                            <p className="text-gray-300 font-semibold text-xs mt-1">a year</p>
                                        </>
                                    )}
                                </div>
                            </div>
                            {/* The three rates as rows, mirroring Aged Care's fee rows — the same
                                shape, so a client who has seen one recognises the other. "was"
                                sits under the rate it replaced rather than in a separate tile. */}
                            <div className="space-y-2.5">
                                {([
                                    ['Clinical Care', 'clinical'],
                                    ['Independence', 'independence'],
                                    ['Everyday Living', 'everydayLiving'],
                                ] as const).map(([label, key]) => {
                                    const now = panelContributionData.contributionRates?.[key] || 0;
                                    const was = beforeContributionData?.contributionRates?.[key];
                                    const moved = showContributionWas && was != null && was !== now;
                                    return (
                                        <div key={key} className="flex items-baseline justify-between gap-3 text-sm">
                                            <span className="text-gray-300">{label}</span>
                                            <span className="text-right">
                                                <span className="font-semibold text-white tabular-nums">{formatPercent(now)}</span>
                                                {moved && (
                                                    <span className="block text-[11px] text-gray-400 tabular-nums">
                                                        was {formatPercent(was)}
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : <div />}
                    financialImpact={panelFinancials ? (
                        <FinancialImpactPanel
                            embedded
                            working={sahRailWorking}
                            before={beforeFinancials as never}
                            after={impactAfterFinancials as never}
                            legacyYears={legacyYears}
                            setLegacyYears={setLegacyYears}
                            onViewProjections={() => setShowProjectionsModal(true)}
                            netWorthAfterValue={impactAfterFinancials?.netWorth ?? null}
                            cashFlowBeforeValue={sahCashFlowBefore}
                            cashFlowAfterValue={sahCashFlowAfter}
                            cashFlowBreakdownOverride={sahCashFlowBreakdown}
                            /* Measured against RECEIVING THE SERVICES WITH NO STRATEGY, projected
                               over the same years. Support at Home is a care requirement, not an
                               elective move, so the alternative is not "don't do it" — it is doing
                               it without a financial plan. Comparing with net worth TODAY credited
                               every strategy with growth that happens either way. */
                            legacyBaselineValue={sahDoNothingLegacy}
                            legacyEstimateOverride={impactProjectedLegacy}
                            baselineLabel={sahDoNothingLegacy != null ? SAH_BENCH.word : undefined}
                        />
                    ) : <div />}
                />
                )}

                {/* THE aged care design, not a copy of it: the shared engine view with charts, the
                    monthly narrative and per-figure tooltips. The old module-specific modal showed the
                    table alone — "matching" the aged care design in layout while missing everything
                    that made it explainable. */}
                {/* The FY blend on row 1 is a FINANCIAL-year device, so calendar mode shows the
                    calendar run's own rows — the choice the Aged Care analyser makes. */}
                {showProjectionsModal && projections && (
                    <ProjectionsModal
                        onClose={() => setShowProjectionsModal(false)}
                        projections={(modalPeriodMode === 'calendar_year' && calendarYearProjections ? calendarYearProjections : (projectionsForModal || projections)) as never}
                        onPeriodModeChange={setModalPeriodMode}
                        isReloading={modalPeriodMode === 'calendar_year' && !calendarYearProjections}
                        factFind={factFind as never}
                        initialFinancials={beforeFinancials as never}
                        currentFinancials={afterFinancials as never}
                        agedCareCosts={(combinedCareCosts || null) as never}
                        costLabel="Support at Home Costs"
                        title={hasStrategies ? manualWords.title : 'The support alone — the same services, nothing else changed'}
                        comparedOverYears={Number(legacyYears) || undefined}
                    />
                )}
            </div>
        </div>
        
        {/* Compare Strategies Modal */}
        {showCompareModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-slate-200/80">
                    <div className="px-6 pt-6 pb-4 border-b border-slate-100 bg-slate-50">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                                    <GradientText>Compare Home Care Strategies</GradientText>
                                </h2>
                                <p className="text-sm text-slate-600 mt-1.5">Side-by-side view of saved Support at Home strategies (model outputs).</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowCompareModal(false)}
                                className="shrink-0 rounded-lg p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors text-2xl leading-none"
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <div className="h-1 w-full rounded-full bg-brand-navy mt-4" aria-hidden />
                    </div>

                    <div className="overflow-auto flex-1 px-6 py-5">
                        <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                            <table className="w-full text-sm border-collapse min-w-[720px]">
                                <thead>
                                    <tr className="bg-slate-100 text-slate-800">
                                        <th className="px-4 py-3 text-left font-semibold border-b border-slate-200/90">Strategy name</th>
                                        <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Age pension</th>
                                        <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Cash flow</th>
                                        <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Legacy estimate</th>
                                        <th className="px-4 py-3 text-right font-semibold border-b border-slate-200/90 whitespace-nowrap">Annual contribution</th>
                                        <th className="px-4 py-3 text-center font-semibold border-b border-slate-200/90 whitespace-nowrap">Clinical care</th>
                                        <th className="px-4 py-3 text-center font-semibold border-b border-slate-200/90 whitespace-nowrap">Independence</th>
                                        <th className="px-4 py-3 text-center font-semibold border-b border-slate-200/90 whitespace-nowrap">Everyday living</th>
                                        <th className="px-4 py-3 text-center font-semibold border-b border-slate-200/90 whitespace-nowrap">Created</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {savedHomeCareStrategies.length === 0 ? (
                                                <tr>
                                                    <td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                                                        No strategies saved yet. Save a strategy to compare.
                                                    </td>
                                                </tr>
                                            ) : (
                                            savedHomeCareStrategies.map((strategy, index) => {
                                                const contributionRates = (strategy.outputs?.contributionRates || {}) as SahContributionRateSet;
                                                const formatPercentModal = (value: number | undefined) => {
                                                    if (!value && value !== 0) return 'N/A';
                                                    return `${(value * 100).toFixed(1)}%`;
                                                };
                                                const cf = Number(strategy.outputs?.cashFlow) || 0;
                                                return (
                                                    <tr
                                                        key={strategy.id}
                                                        className={`border-b border-slate-100 last:border-b-0 transition-colors hover:bg-sky-50/50 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}`}
                                                    >
                                                        <td className="px-4 py-3 font-medium text-slate-900 align-top">
                                                            {strategy.name}
                                                            {strategy.source === 'suggestion_engine' ? (
                                                                <span className="block text-xs font-normal text-slate-500">Suggestion engine</span>
                                                            ) : null}
                                                            {/* The contribution column is budget x rate. Change the services
                                                                afterwards and the rate still holds while the dollars do not,
                                                                so a strategy priced on a different plan says so HERE — beside
                                                                its own name, where the figures are being compared. */}
                                                            {(() => {
                                                                const warning = servicePlanBasisWarning(
                                                                    strategy.outputs?.servicePlanBasis as ServicePlanBasis | undefined,
                                                                    currentServicePlanBasis,
                                                                );
                                                                return warning ? (
                                                                    <span className="mt-1 block rounded border border-slate-200 border-l-4 border-l-amber-400 bg-white px-2 py-1 text-[11px] font-normal leading-snug text-slate-700">
                                                                        {warning}
                                                                    </span>
                                                                ) : null;
                                                            })()}
                                                        </td>
                                                        <td className="px-4 py-3 text-right tabular-nums text-slate-800">{formatCurrency(Number(strategy.outputs?.agePension) || 0)}</td>
                                                        <td className={`px-4 py-3 text-right tabular-nums font-medium ${cf < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{formatCurrency(cf)}</td>
                                                        <td className="px-4 py-3 text-right tabular-nums text-slate-800">{formatCurrency(Number(strategy.outputs?.legacyEstimate) || 0)}</td>
                                                        <td className="px-4 py-3 text-right tabular-nums text-slate-800">{formatCurrency(strategy.outputs?.annualContribution || 0)}</td>
                                                        <td className="px-4 py-3 text-center">{formatPercentModal(contributionRates.clinical)}</td>
                                                        <td className="px-4 py-3 text-center">{formatPercentModal(contributionRates.independence)}</td>
                                                        <td className="px-4 py-3 text-center">{formatPercentModal(contributionRates.everydayLiving)}</td>
                                                        <td className="px-4 py-3 text-center text-slate-600 align-top">
                                                            {strategy.createdAt ? new Date(String(strategy.createdAt)).toLocaleDateString() : ''}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                            )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <p className="text-xs text-slate-500">Select a strategy in Optimise Your Plan to view the explainer and customise details.</p>
                        <Button
                            type="button"
                            onClick={() => setShowCompareModal(false)}
                            variant="secondary"
                            className="shrink-0 self-end sm:self-auto"
                        >
                            Close
                        </Button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};
export default StrategiesSection;